// src/prototype/data/execute/vendorLandscapeData.ts
// ============================================================
// RESEARCHED VENDOR LANDSCAPES (Oct 21, 2026) for the three Execute demo use cases.
// Generated from sourced research: every vendor carries at least one source that was opened.
// Time to deploy and cost appear only where a public source states them (else null).
// Outcome figures in `evidence` are vendor-reported unless the source is independent.
// Completeness over caution: competitors and potential acquirers are included
// (competitorFlag is internal metadata and never filters the view).
// Reconciled three ways (Oct 21): Claude research + ChatGPT and Gemini deep research. Vendors
// proposed by ChatGPT were re-verified against their sources before inclusion; Gemini added no
// new vendors. `foundBy` records which research pass surfaced each vendor.
// DO NOT hand-edit vendor facts without a source; regenerate from the research JSON.
// ============================================================
import type { VendorLandscape } from './vendorLandscape';

export const RESEARCHED_LANDSCAPES: Record<string, Omit<VendorLandscape, 'useCaseId'>> = {
  "opp-1": {
    "asOf": "2026-10-21",
    "generation": "pre-generated, reviewed by hand",
    "categories": [
      {
        "name": "Care management and transitions applications",
        "covers": "Standalone care management platforms that ingest ADT, claims and clinical data, risk-stratify patients, and run transitional care management (TCM), chronic care management and outreach worklists for care managers.",
        "deploymentModels": [
          "SaaS"
        ],
        "fitConsiderations": "For an Epic IDN these overlap with Epic Healthy Planet/Compass Rose; the case for a third-party tool usually rests on multi-EHR affiliate networks, claims integration across many payer contracts, or care-team workflow outside Epic. Several vendors in this category are payer-oriented (e.g., ZeOmega) and should be checked for provider-side references."
      },
      {
        "name": "ADT event notification networks/services",
        "covers": "Networks that route real-time admit, discharge and transfer events (including from out-of-network hospitals and SNFs) to PCPs, care managers, ACOs and post-acute providers, supporting the CMS patient event notification Conditions of Participation.",
        "deploymentModels": [
          "network service",
          "SaaS"
        ],
        "fitConsiderations": "Value depends on network coverage in the client's market, including non-Epic hospitals and SNFs. State and regional HIEs (e.g., Manifest MedEx in California) are often an alternative or complementary feed and should be evaluated alongside commercial networks."
      },
      {
        "name": "Post-acute network management / referral placement and discharge orchestration",
        "covers": "Electronic referral to SNF, home health, IRF, LTACH and community providers; patient-choice compliance; payer authorization; post-acute performance tracking; and inpatient discharge-planning automation.",
        "deploymentModels": [
          "SaaS",
          "network service",
          "managed service"
        ],
        "fitConsiderations": "Key differentiators are depth of embedding in Epic Case Management, size and responsiveness of the receiving-provider network, and post-acute quality/length-of-stay data for preferred-network decisions."
      },
      {
        "name": "EHR-native care coordination modules",
        "covers": "Care management, population health registries, risk scores and outreach built into the EHR (Epic Healthy Planet/Compass Rose, Oracle Health population health, MEDITECH Expanse care coordination).",
        "deploymentModels": [
          "EHR module"
        ],
        "fitConsiderations": "For an Epic client the native modules are the default baseline; license and build effort are internal and not publicly priced. Gaps typically cited are data from non-Epic affiliates and post-acute partners and the analytics depth needed for contract-level prioritization."
      },
      {
        "name": "Patient outreach / engagement services",
        "covers": "Automated post-discharge texts, calls and AI voice agents; nurse call-back programs; digital care pathways; escalation of patient-reported issues to care teams.",
        "deploymentModels": [
          "SaaS",
          "managed service"
        ],
        "fitConsiderations": "Evaluate write-back into Epic (In Basket, flowsheets), escalation logic, language coverage and whether published outcomes are from the vendor or peer-reviewed. Health Catalyst's Twistle also sits in this category (listed under Health Catalyst)."
      },
      {
        "name": "Readmission risk analytics / population health analytics and benchmarking",
        "covers": "Risk models for readmission and utilization, population health analytics, contract performance and peer benchmarking of readmission and post-acute metrics.",
        "deploymentModels": [
          "SaaS",
          "hosted",
          "managed service"
        ],
        "fitConsiderations": "Most directly overlaps with an analytics prioritization product; several vendors here are flagged as competitors or potential acquirers. Check model transparency, local calibration against Epic's native readmission model, and how scores reach care-team worklists."
      },
      {
        "name": "Value-based care enablement and managed care-management services",
        "covers": "Organizations that provide risk-contract enablement, care management staffing, analytics and ACO operations, either for health systems or for independent practices and health plans.",
        "deploymentModels": [
          "managed service",
          "SaaS"
        ],
        "fitConsiderations": "Relevant when the IDN wants to outsource or co-source risk-contract operations; several are primarily payer- or independent-practice-facing and may be competitors to the IDN's own clinically integrated network rather than vendors to it."
      },
      {
        "name": "Community and social care referral networks",
        "covers": "Closed-loop referral to community-based organizations (food, housing, transportation) as part of discharge and transitions workflows.",
        "deploymentModels": [
          "SaaS",
          "network service"
        ],
        "fitConsiderations": "Epic has a Community Resource Network Toolbox category; confirm whether the vendor is embedded in Epic and what share of local CBOs actively accept referrals."
      },
      {
        "name": "Horizontal CRM and cloud platforms",
        "covers": "General-purpose CRM/cloud platforms with healthcare data models used to build care coordination, contact center and outreach workflows around the EHR.",
        "deploymentModels": [
          "SaaS",
          "hosted"
        ],
        "fitConsiderations": "Flexible but build-heavy; typically implemented through systems integrators. Best suited where the IDN already standardizes on the platform for patient access or contact center work."
      },
      {
        "name": "Remote patient monitoring and post-discharge virtual care",
        "covers": "Remote monitoring and virtual check-ins after discharge, with clinician dashboards, escalation rules and device logistics.",
        "deploymentModels": [
          "SaaS",
          "Managed service"
        ],
        "fitConsiderations": "Who staffs the monitoring, device logistics for the patient population, and whether readings and alerts flow into Epic rather than a separate portal."
      }
    ],
    "vendors": [
      {
        "name": "Innovaccer",
        "parentCompany": null,
        "category": "Care management and transitions applications",
        "capabilitySummary": "Healthcare data and analytics platform with a care management suite that unifies EHR, lab and claims data into patient profiles and automates transitional care workflows for Medicare Advantage, MSSP and commercial populations.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Sources reviewed do not document an Epic Showroom/Toolbox listing. The 2026 KLAS release names Orlando Health, Adventist HealthCare and Banner Health as customers; EHR use at those sites was not verified from the sources.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "2026 Best in KLAS: Data Analytics Platform for Providers (Gravity, 93.2), CRM (Cured, 90.1), Data Analytics Platform for Payers (Galaxy, 90.5)",
          "#3 in 2025 Best in KLAS Population Health Management (85.7)",
          "Franciscan Alliance care management case study"
        ],
        "sources": [
          {
            "title": "Franciscan Alliance: Improving Care Management (Innovaccer case study)",
            "url": "https://innovaccer.ai/resources/case-studies/franciscan-alliance-improving-care-management-solution-suite",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Innovaccer Achieves Best in KLAS Across Data & AI Platform for Providers, Payer AI, and CRM (Business Wire via 01net)",
            "url": "https://www.01net.it/innovaccer-achieves-best-in-klas-across-data-ai-platform-for-providers-payer-ai-and-crm",
            "retrieved": "2026-10-21"
          },
          {
            "title": "2025 Best in KLAS: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Lightbeam Health Solutions",
        "parentCompany": null,
        "category": "Care management and transitions applications",
        "capabilitySummary": "Population health and care management platform with real-time ADT ingestion and rules that flag discharged patients for TCM, chronic care and rising-risk programs, plus AI risk stratification and automated SMS/IVR outreach. Acquired Jvion (2022) and Syntax Health (Dec 2025).",
        "deploymentModel": "SaaS",
        "integrationNotes": "Vendor site cites point-of-care integration in EHR workflows and support for 50 EHRs; Epic is not named in sources reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "#4 in 2025 Best in KLAS Population Health Management (82.7)",
          "Best in KLAS 2025 Remote Patient Monitoring",
          "Saint Peter's University Hospital reported 7% ED visit reduction (vendor newsroom, Jul 2025)"
        ],
        "sources": [
          {
            "title": "Lightbeam Health Solutions home page",
            "url": "https://lightbeamhealth.com/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Lightbeam Newsroom",
            "url": "https://lightbeamhealth.com/newsroom/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Lightbeam scoops up Jvion to expand AI, SDOH analytics capabilities (Fierce Healthcare)",
            "url": "https://www.fiercehealthcare.com/health-tech/lightbeam-scoops-jvion-expand-ai-sdoh-analytics-capabilities",
            "retrieved": "2026-10-21"
          },
          {
            "title": "2025 Best in KLAS: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Arcadia",
        "parentCompany": "Nordic Capital (majority owner; deal announced July 2025)",
        "category": "Care management and transitions applications",
        "capabilitySummary": "Healthcare data platform (Ascent) combining clinical, claims and SDoH data; Ascent Care adds AI patient prioritization, care plans, task routing and automated outreach for providers, payers and ACOs. Has resold PatientPing ADT notifications to drive care management enrollment.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Ascent Care page does not describe Epic integration. Arcadia has resold PatientPing (now Bamboo Health) ADT feeds into its platform (2020 release).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "#5 in 2025 Best in KLAS Population Health Management (79.7)",
          "KLAS 2021 Population Health Vendor Overview named Arcadia among the most-considered vendors",
          "customers cited include Intermountain Health, Ochsner Health, Johns Hopkins, UChicago Medicine"
        ],
        "sources": [
          {
            "title": "Arcadia Ascent Care",
            "url": "https://arcadia.io/ascent/care/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Nordic Capital partners with Arcadia (Healthcare IT Today)",
            "url": "https://www.healthcareittoday.com/2025/07/21/nordic-capital-partners-with-arcadia-to-drive-data-focused-healthcare-innovation/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Private equity firm buys health data analytics company Arcadia (HealthExec)",
            "url": "https://healthexec.com/topics/healthcare-management/mergers-and-acquisitions/private-equity-firm-buys-health-data-analytics-company-arcadia",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Arcadia Expands Partnership with PatientPing",
            "url": "https://bamboohealth.com/news/arcadia-expands-partnership-with-patientping-enhancing-access-to-real-time-patient-data-for-healthcare-organizations/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Elligint Health (HealthEC + VirtualHealth HELIOS)",
        "parentCompany": null,
        "category": "Care management and transitions applications",
        "capabilitySummary": "Company formed in 2024 by combining HealthEC (population health management) and VirtualHealth (HELIOS medical management platform for care, utilization and population health management) for payers and providers.",
        "deploymentModel": "SaaS",
        "integrationNotes": "HELIOS described as FHIR-interoperable; no Epic-specific evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Listed (unranked, 71.1) as 'Elligint Health HealthEC Population Health Management Suite' in 2025 Best in KLAS Population Health Management",
          "HealthEC Best in KLAS 2019 and 2022 per company release"
        ],
        "sources": [
          {
            "title": "HealthEC and VirtualHealth Unite to Launch Elligint Health (Business Wire)",
            "url": "https://www.businesswire.com/news/home/20250109141307/en/HealthEC-and-VirtualHealth-Unite-to-Launch-Elligint-Health-Redefining-the-Future-of-Healthcare-with-Intelligent-Solutions",
            "retrieved": "2026-10-21"
          },
          {
            "title": "2025 Best in KLAS: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "ZeOmega (Jiva)",
        "parentCompany": null,
        "category": "Care management and transitions applications",
        "capabilitySummary": "Jiva population health and care management platform with clinical content, rules engine and member texting; customers are mainly health plans and other risk-bearing organizations.",
        "deploymentModel": "SaaS",
        "integrationNotes": "No Epic integration evidence found in sources reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "#1 Best in KLAS Payer Care Management, 2025 (fourth consecutive year). Provider/IDN references not confirmed"
        ],
        "sources": [
          {
            "title": "ZeOmega Named #1 Best in KLAS for Payer Care Management (Michigan Association of Health Plans)",
            "url": "https://www.mahp.org/2025/02/14/zeomega-named-1-best-in-klas-for-payer-care-management-solutions-for-fourth-consecutive-year/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Persivia (CareSpace)",
        "parentCompany": null,
        "category": "Care management and transitions applications",
        "capabilitySummary": "CareSpace population health platform with the Soliton rules/AI engine for in-workflow alerts, care gap management, HCC risk adjustment and post-discharge remote monitoring.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Prime Healthcare (46 hospitals) described CareSpace as integrated with multiple EHRs including Epic, MEDITECH, NextGen and eClinicalWorks (2020 release).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Prime Healthcare multi-year customer per Dec 2020 press release. Source is dated",
          "current scope not re-verified"
        ],
        "sources": [
          {
            "title": "Persivia expands solutions with Prime Healthcare Services",
            "url": "https://wp.persivia.com/press-release/persivia-expands-solutions-with-prime-healthcare-services-to-include-hcc-risk-adjustment-star-ratings-management-and-comprehensive-virtual-care/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "low",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Andor Health (ThinkAndor)",
        "parentCompany": null,
        "category": "Care management and transitions applications",
        "capabilitySummary": "ThinkAndor platform of AI agents for virtual care, patient monitoring, care team collaboration and transitions in care, including post-discharge monitoring.",
        "deploymentModel": "SaaS",
        "integrationNotes": "ThinkAndor Remote Patient Monitoring holds Epic Toolbox designation in the RPM category (Aug 2026); data flows into the Epic record.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Customers named on vendor site include Tampa General, Ohio State, Ballad Health, MUSC, Sentara",
          "UPMC expanded an AI transitional care model with Andor and Psynergy Health to 18 hospitals (vendor news, Aug 2026). Vendor reports a 38% readmission reduction in a monitoring case study"
        ],
        "sources": [
          {
            "title": "Andor Health home page",
            "url": "https://andorhealth.com/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Andor Health Integrates AI-Native Remote Patient Monitoring with Epic (MedTech Spectrum)",
            "url": "https://medtechspectrum.com/news/15/25919/andor-health-integrates-ai-native-remote-patient-monitoring-with-epic-.html",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "PointClickCare (incl. Collective Medical and Audacious Inquiry)",
        "parentCompany": null,
        "category": "ADT event notification networks/services",
        "capabilitySummary": "Care collaboration network combining Collective Medical's hospital/ED network and Audacious Inquiry's notification services with PointClickCare's post-acute EHR data, providing real-time event notifications across hospitals, SNFs, health plans and HIEs.",
        "deploymentModel": "network service",
        "integrationNotes": "Vendor page states hospital connectivity works regardless of EHR; Epic-specific integration not described in sources reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Vendor cites 27,000 senior care facilities, 2,700+ hospitals, 180 health plans and 75 state/government agencies"
        ],
        "sources": [
          {
            "title": "Collective Medical is now part of PointClickCare",
            "url": "https://pointclickcare.com/collective-medical/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Bamboo Health (Pings, formerly PatientPing)",
        "parentCompany": null,
        "category": "ADT event notification networks/services",
        "capabilitySummary": "Care coordination suite including Pings real-time ADT notifications, rising-risk identification, automated discharge summaries, transition tracking and point-of-care patient history across in- and out-of-network encounters.",
        "deploymentModel": "network service",
        "integrationNotes": "Described as EHR-agnostic with workflow integration for EHRs and HIEs; Epic not named in sources reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "WakeMed (Raleigh, NC) selected the suite across 3 hospitals and 8 EDs (June 2024). Vendor cites coverage of 52 states/territories"
        ],
        "sources": [
          {
            "title": "Bamboo Health home page",
            "url": "https://bamboohealth.com/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "WakeMed Selects Bamboo Health to Drive Improved Care Coordination (Business Wire via Silicon UK)",
            "url": "https://www.silicon.co.uk/press-release/wakemed-selects-bamboo-health-to-drive-improved-care-coordination-for-patients",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "WellSky CarePort",
        "parentCompany": "WellSky",
        "category": "Post-acute network management / referral placement and discharge orchestration",
        "capabilitySummary": "CarePort Community Referral Network for electronic referral from hospital case management to SNF, home health, IRF, LTACH, DME, transport and social care providers; CarePort Interop sends ADT-based patient event notifications to PCPs and post-acute providers to meet CMS CoP requirements.",
        "deploymentModel": "SaaS / network service",
        "integrationNotes": "CarePort Community Referral Network joined Epic Toolbox (Community Resource Network category, Dec 2025), embedded in Epic Case Management. CarePort Interop stated as available in the Epic App Orchard marketplace.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Vendor cites 130,000+ post-acute and community providers in network, average referral acceptance of 24 minutes, 1,000 hospitals and 25M annual referrals (Interop page)"
        ],
        "sources": [
          {
            "title": "WellSky Joins Epic Toolbox ... Community Resource Network (Business Wire)",
            "url": "https://www.businesswire.com/news/home/20251210760242/en/WellSky-Joins-Epic-Toolbox-to-Improve-Patient-Care-Transitions-and-Coordination-Through-the-Community-Resource-Network",
            "retrieved": "2026-10-21"
          },
          {
            "title": "WellSky integrates CarePort network into Epic Toolbox (HomeCare)",
            "url": "https://www.homecaremag.com/news/wellsky-integrates-careport-network-epic-toolbox",
            "retrieved": "2026-10-21"
          },
          {
            "title": "CarePort Interop",
            "url": "https://careporthealth.com/products/interop/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Aidin",
        "parentCompany": "LeanTaaS (acquired Sept 2026; LeanTaaS majority-owned by Bain Capital since 2022)",
        "category": "Post-acute network management / referral placement and discharge orchestration",
        "capabilitySummary": "Care transition platform for post-acute referral broadcasting, payer prior authorization, patient-choice compliance and messaging with post-acute facilities; being integrated into LeanTaaS iQueue for Inpatient Flow.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Vendor site has an 'Aidin for Epic Users' referral-management integration page; details not verified. No Toolbox listing found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "200+ hospitals across about 20 health systems, 2M+ referrals/year, 21,000+ post-acute providers",
          "Emory Healthcare selected Aidin for 10 hospitals (June 2025)",
          "AdventHealth, St. Luke's and Edward-Elmhurst success stories on vendor site"
        ],
        "sources": [
          {
            "title": "LeanTaaS Acquires Aidin (HIT Consultant)",
            "url": "https://hitconsultant.net/2026/09/01/leantaas-acquires-aidin/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "LeanTaaS acquires Aidin to ease hospital discharges (MobiHealthNews)",
            "url": "https://www.mobihealthnews.com/news/leantaas-acquires-aidin-ease-hospital-discharges",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Emory Healthcare selects Aidin",
            "url": "https://www.myaidin.com/news_press/emory-healthcare-care-management/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Aidin home page",
            "url": "https://www.myaidin.com/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Real Time Medical Systems",
        "parentCompany": null,
        "category": "Post-acute network management / referral placement and discharge orchestration",
        "capabilitySummary": "Post-acute and SNF interventional analytics that extract SNF EHR data for hospital-post-acute collaboration on readmissions, length of stay and care transitions, with a LACE-based rehospitalization risk score and network management views.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Integrates with post-acute EHRs (partners listed include MatrixCare); no Epic-specific evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Vendor cites 2,300+ facilities and a Maryland Total Cost of Care initiative",
          "vendor-reported 52% average readmission reduction (not independently verified)"
        ],
        "sources": [
          {
            "title": "Real Time Medical Systems home page",
            "url": "https://realtimemed.com/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Qventus",
        "parentCompany": null,
        "category": "Post-acute network management / referral placement and discharge orchestration",
        "capabilitySummary": "Inpatient discharge-planning automation using ML on clinical notes to set expected discharge dates and disposition, prioritize case management worklists and track discharge milestones within EHR workflows.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Release states the product is embedded in EHR workflows; Epic not named in the source reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "OhioHealth and HonorHealth results in vendor release (July 2024)",
          "vendor-reported 20-35% reduction in excess days"
        ],
        "sources": [
          {
            "title": "Qventus Launches its 3rd Generation Inpatient Solution",
            "url": "https://www.qventus.com/company/newsroom/qventus-launches-its-3rd-generation-inpatient-solution-to-optimize-hospital-discharge-planning-and-automate-care-planning-fully-embedded-into-ehr-workflows/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Optum Home & Community Care (formerly naviHealth)",
        "parentCompany": "Optum / UnitedHealth Group",
        "category": "Post-acute network management / referral placement and discharge orchestration",
        "capabilitySummary": "Technology-enabled care management for patients at home and in post-acute facilities; the naviHealth name was retired in favor of Home & Community Care starting Q1 2024.",
        "deploymentModel": "managed service",
        "integrationNotes": "No Epic integration evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Rebrand followed congressional criticism of algorithm use in post-acute coverage decisions (STAT, Oct 2023). Current health-system-facing offerings not verified"
        ],
        "sources": [
          {
            "title": "UnitedHealth's Optum drops NaviHealth name (STAT)",
            "url": "https://www.statnews.com/2023/10/23/unitedhealth-optum-navihealth-rebranding-algorithm/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Epic (Healthy Planet, Compass Rose, Care Management)",
        "parentCompany": null,
        "category": "EHR-native care coordination modules",
        "capabilitySummary": "Healthy Planet population health (claims and external data aggregation, contract performance, outreach campaigns, risk stratification) and Compass Rose care coordination (program enrollment, care plans, milestones, task worklists), plus an Epic cognitive computing 30-day readmission risk model.",
        "deploymentModel": "EHR module",
        "integrationNotes": "Native to Epic. Corewell Health combined Epic's readmission risk model, Compass Rose and Reporting Workbench in a transitions-of-care program.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "#2 in 2025 Best in KLAS Population Health Management (Healthy Planet, 89.2). Corewell Health HIMSS Davies submission: high-risk value-based cohort readmission rate fell from 23% to 7%"
        ],
        "sources": [
          {
            "title": "Epic population health software",
            "url": "https://www.epic.com/software/population-health/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Corewell Health: Reducing Readmission Risk (HIMSS Davies case study)",
            "url": "https://arkansas.himss.org/sites/hde/files/media/file/2024/10/31/corewell-health-reducing-readmission-risk-_1.pdf",
            "retrieved": "2026-10-21"
          },
          {
            "title": "2025 Best in KLAS: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Oracle Health (Health Data Intelligence / population health)",
        "parentCompany": "Oracle",
        "category": "EHR-native care coordination modules",
        "capabilitySummary": "EHR-agnostic health data platform with Care Coordination Intelligence (AI record summaries, outreach, care gap closure) and Quality and Contract Management that flags readmission risk.",
        "deploymentModel": "EHR module / SaaS",
        "integrationNotes": "Described as EHR-agnostic, integrating Oracle Health Millennium and third-party systems; no Epic-specific evidence.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Customer stories on vendor page: Henry Community Health, Margaret Mary Health, Marshall Health Network, Wray Community District Hospital"
        ],
        "sources": [
          {
            "title": "Oracle Health population health",
            "url": "https://www.oracle.com/health/population-health/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "MEDITECH (Expanse Care Coordination / Population Health)",
        "parentCompany": null,
        "category": "EHR-native care coordination modules",
        "capabilitySummary": "Expanse Case Management (admission-to-discharge coordination, utilization review with MCG/InterQual), Community Care Transitions Portal for post-acute communication, Care Compass registries, and Traverse Exchange for cross-EHR data sharing.",
        "deploymentModel": "EHR module",
        "integrationNotes": "Not applicable to an Epic client except where affiliates run MEDITECH; Traverse Exchange shares data with other EHR vendors.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "No customer outcomes named on the care coordination page reviewed"
        ],
        "sources": [
          {
            "title": "MEDITECH Care Coordination",
            "url": "https://ehr.meditech.com/ehr-solutions/care-coordination",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Artera (formerly WELL Health)",
        "parentCompany": null,
        "category": "Patient outreach / engagement services",
        "capabilitySummary": "Patient communications platform with automated post-discharge text/call sequences, collaborative inbox for care teams and workflow analytics; Care Navigator product for clinical and post-discharge communication.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Vendor lists EHR integrations generally; Epic specifics not verified in sources reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "2026 Best in KLAS Patient Communications",
          "Vanderbilt University Hospital Discharge Care Center (80,000+ discharges) 30-day readmissions fell from 10.6% to 9.9% (NEJM Catalyst study as summarized by vendor)",
          "Houston Methodist BMJ study cited. Vendor cites 1,000+ healthcare organizations"
        ],
        "sources": [
          {
            "title": "Vanderbilt study on post-discharge communication (Artera blog)",
            "url": "https://artera.io/blog/vanderbilt-study-post-discharge-communication/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Artera Best in KLAS 2026",
            "url": "https://artera.io/blog/best-in-klas/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Artera Care Navigator",
            "url": "https://artera.io/care-navigator/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "CipherHealth",
        "parentCompany": null,
        "category": "Patient outreach / engagement services",
        "capabilitySummary": "Conversational AI agents and post-discharge follow-up call programs (automated and nurse-staffed) that check discharge understanding, prescription access and medication questions, escalating patients needing intervention.",
        "deploymentModel": "SaaS / managed service",
        "integrationNotes": "No EHR named in the case study reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Intermountain Healthcare post-discharge follow-up program: 6.4K patients called, 43% required intervention, savings described as 'just under $15M' (vendor case study)"
        ],
        "sources": [
          {
            "title": "Achieving $15M in savings by reducing readmissions with post-discharge follow-up (CipherHealth)",
            "url": "https://cipherhealth.com/resource/case-study/achieving-15m-in-savings-by-reducing-readmissions-with-post-discharge-follow-up/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Luma Health",
        "parentCompany": null,
        "category": "Patient outreach / engagement services",
        "capabilitySummary": "Patient access and engagement platform with an AI voice/self-service agent (Navigator), fax-to-EHR routing and workflow automations including post-discharge and readmission follow-up calls.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Vendor states integration with Epic In Basket, Media Manager and order entry, with write-back to Epic. Showroom/Toolbox status not stated.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Named Epic customers include UF Health, UAMS, Virtua Health, UW Medicine, University of Vermont Health Network",
          "vendor cites $1M staff cost savings at Virtua in year one"
        ],
        "sources": [
          {
            "title": "Luma Health Epic integration",
            "url": "https://go.lumahealth.io/epic/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Get Well",
        "parentCompany": null,
        "category": "Patient outreach / engagement services",
        "capabilitySummary": "Digital patient engagement including Digital Transitional Care Management: procedure-specific care plans, virtual check-ins and education at home, real-time risk flagging and outcomes analytics.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Vendor states integration with major EHRs including Epic and Oracle.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Vendor case study reports 45.6% reduction in readmissions and 66% reduction in SNF referrals for Guided Care users (customers not named)"
        ],
        "sources": [
          {
            "title": "Unlocking Value-Based Care Performance with Precision Patient Engagement (Get Well case study)",
            "url": "https://www.getwellnetwork.com/resource/case-study-improve-value-based-care-performance/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Commure Engage (formerly Memora Health)",
        "parentCompany": "Commure (acquisition announced Dec 20, 2024)",
        "category": "Patient outreach / engagement services",
        "capabilitySummary": "Care enablement platform automating high-touch clinical workflows: two-way patient-care team messaging, routing of patient-reported concerns and patient navigation pathways.",
        "deploymentModel": "SaaS",
        "integrationNotes": "No Epic evidence in source reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Acquisition by Commure reported by Healthcare Finance News (Dec 2024). No customers named in source"
        ],
        "sources": [
          {
            "title": "Commure acquires digital care navigator Memora Health (Healthcare Finance News)",
            "url": "https://www.healthcarefinancenews.com/news/commure-acquires-digital-care-navigator-memora-health",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Commure acquires Memora Health",
            "url": "https://www.commure.com/press-releases/commure-acquires-memora-health-to-enhance-intelligent-care-navigation",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Memora Health (now part of Commure)",
            "url": "https://www.memorahealth.com/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Commure Engage",
            "url": "https://www.commure.com/engage",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Health Catalyst (incl. Twistle)",
        "parentCompany": null,
        "category": "Readmission risk analytics / population health analytics and benchmarking",
        "capabilitySummary": "Data and analytics platform (Ignite) and population health applications; Twistle provides automated post-discharge patient messaging with EHR-inbox escalation. Acquired Upfront Healthcare Services (announced Jan 2025, $86M).",
        "deploymentModel": "SaaS / hosted / managed service",
        "integrationNotes": "Twistle alerts care teams via EHR inbox; Epic not named in sources reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Population Health Applications listed (unranked, 80.6) in 2025 Best in KLAS Population Health",
          "Twistle post-discharge program at a large health system: O/E readmission ratio 1.13 to 0.81 (Nov 2019-Nov 2020). Ben Albert became CEO Feb 2026",
          "1,100+ client organizations"
        ],
        "sources": [
          {
            "title": "Twistle Patient Engagement",
            "url": "https://www.healthcatalyst.com/products/twistle-patient-engagement",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Preventing Readmissions with Post-discharge Monitoring",
            "url": "https://healthcatalyst.com/learn/success-stories/post-discharge-monitoring",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Health Catalyst appoints Ben Albert as CEO",
            "url": "https://healthcatalyst.com/news/health-catalyst-appoints-ben-albert-as-chief-executive-officer",
            "retrieved": "2026-10-21"
          },
          {
            "title": "New Leadership, Partnerships, and Platforms Mark Health Catalyst's Year (HCI)",
            "url": "https://www.hcinnovationgroup.com/finance-revenue-cycle/article/55336749/new-leadership-partnerships-and-platforms-mark-health-catalysts-transformative-year",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "ClosedLoop",
        "parentCompany": null,
        "category": "Readmission risk analytics / population health analytics and benchmarking",
        "capabilitySummary": "Healthcare data science platform for patient risk forecasting (e.g., readmission and utilization) intended to help organizations improve outcomes and reduce costs.",
        "deploymentModel": "SaaS",
        "integrationNotes": "No Epic-specific evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "#1 in Healthcare AI: Data Science Solutions, 2025 Best in KLAS",
          "$34M Series B (2021). Ownership changes since 2021 not confirmed"
        ],
        "sources": [
          {
            "title": "2025 Best in KLAS highlights ClosedLoop for healthcare AI (TechTarget)",
            "url": "https://www.techtarget.com/healthtechanalytics/news/366618972/Best-in-KLAS-highlights-ClosedLoop-for-healthcare-AI",
            "retrieved": "2026-10-21"
          },
          {
            "title": "ClosedLoop.ai raises $34M Series B (HIT Consultant)",
            "url": "https://hitconsultant.net/?p=62682",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Vizient (Clinical Data Base)",
        "parentCompany": null,
        "category": "Readmission risk analytics / population health analytics and benchmarking",
        "capabilitySummary": "Clinical Data Base for peer benchmarking, custom readmission reports and dashboards, combined with improvement collaboratives (e.g., High Utilizer Collaborative) and analytic improvement managers.",
        "deploymentModel": "SaaS / managed service",
        "integrationNotes": "Benchmarking data submission; no Epic-specific evidence reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "RUSH University System for Health: 27 fewer readmissions and about $694,000 estimated savings over 12 months (vendor case study)"
        ],
        "sources": [
          {
            "title": "RUSH readmission reduction (Vizient client success)",
            "url": "https://www.vizient.com/client-success/rush-readmission-reduction",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Premier (PINC AI)",
        "parentCompany": "Patient Square Capital (take-private completed Nov 2025, $2.6B)",
        "category": "Readmission risk analytics / population health analytics and benchmarking",
        "capabilitySummary": "Data, analytics, consulting and supply chain company; technology offerings (PINC AI) include clinical decision support and analytics that embed evidence-based guidance in clinical workflows. Risk-adjusted readmission benchmarking was added to its platform in 2012.",
        "deploymentModel": "SaaS / managed service",
        "integrationNotes": "No Epic-specific evidence reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Current readmission-specific product names not confirmed from Premier's technology page",
          "readmissions capability source is dated (2012)"
        ],
        "sources": [
          {
            "title": "Patient Square Capital completes take-private of Premier (Ropes & Gray)",
            "url": "https://www.ropesgray.com/en/news-and-events/news/2025/11/patient-square-capital-completes-take-private-acquisition-of-premier-inc",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Premier technology",
            "url": "https://premierinc.com/what-we-do/technology",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Health systems to better reduce readmissions (Fierce Healthcare, 2012)",
            "url": "https://www.fiercehealthcare.com/it/health-systems-to-better-reduce-readmissions-manage-labor-productivity-across-continuum-care",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Cedar Gate Technologies",
        "parentCompany": "IQVIA (deal reported Oct 2025, ~$750M per Axios)",
        "category": "Readmission risk analytics / population health analytics and benchmarking",
        "capabilitySummary": "Value-based care platform for payers, providers and employers covering data management, analytics, care management and payment administration.",
        "deploymentModel": "SaaS",
        "integrationNotes": "No Epic-specific evidence reviewed.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Noted for customer success in KLAS 2021 Population Health Vendor Overview (as Cedar Gate/Enli). Deal close not confirmed from a primary IQVIA source"
        ],
        "sources": [
          {
            "title": "Scoop: Iqvia to pay $750M for Cedar Gate Technologies (Axios Pro)",
            "url": "https://www.axios.com/pro/health-tech-deals/2025/10/08/iqvia-cedar-gate-technologies-750m",
            "retrieved": "2026-10-21"
          },
          {
            "title": "IQVIA acquires Cedar Gate Technologies (Signalbase)",
            "url": "https://www.trysignalbase.com/news/acquisitions/cedar-gate-technologies-acquired-by-iqvia-acquisition",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Epic Systems Pulls Ahead for Population Health Management Vendors (TechTarget, 2021)",
            "url": "https://www.techtarget.com/healthtechanalytics/news/366591170/Epic-Systems-Pulls-Ahead-for-Population-Health-Management-Vendors",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Lumeris",
        "parentCompany": null,
        "category": "Value-based care enablement and managed care-management services",
        "capabilitySummary": "Value-based care enablement for health systems: contracting and governance services, high-risk patient management, acute/post-acute orchestration to reduce readmissions, documentation and coding services, and the Tom AI primary-care platform.",
        "deploymentModel": "managed service / SaaS",
        "integrationNotes": "Vendor cites 219+ payer/EHR data pipelines; Epic not named.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Vendor-reported 20-30% total cost of care reduction (not independently verified)",
          "no health system customers named on page"
        ],
        "sources": [
          {
            "title": "Lumeris: How we help",
            "url": "https://www.lumeris.com/how-we-help/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Aledade",
        "parentCompany": null,
        "category": "Value-based care enablement and managed care-management services",
        "capabilitySummary": "Network of independent primary care practices in value-based contracts that uses HIE-delivered ADT notifications to trigger transitional care management and ED follow-up.",
        "deploymentModel": "managed service",
        "integrationNotes": "Receives ADTs via HIEs such as Manifest MedEx rather than direct hospital interfaces.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "In California in 2022, HIE ADTs triggered about 2,000 TCM and 2,400 ED follow-ups with an estimated $4.2M savings (Medical Economics, Dec 2023). Primarily serves independent practices",
          "may compete with an IDN's own CIN"
        ],
        "sources": [
          {
            "title": "Can hospital ADT notifications change the game for value-based care? (Medical Economics)",
            "url": "https://www.medicaleconomics.com/view/can-hospital-adt-notifications-change-the-game-for-value-based-care-one-aledade-case-study-shows-what-is-possible",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Evolent",
        "parentCompany": null,
        "category": "Value-based care enablement and managed care-management services",
        "capabilitySummary": "Specialty care management (oncology, cardiology, musculoskeletal, radiology and others) and member navigation, sold mainly to health plans; also operates a primary care ACO for independent providers.",
        "deploymentModel": "managed service",
        "integrationNotes": "No Epic evidence; health-plan-facing.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Vendor site positions offerings for health plans",
          "transitions-of-care offerings not described. Limited direct fit for an IDN care transitions program"
        ],
        "sources": [
          {
            "title": "Evolent home page",
            "url": "https://www.evolent.com/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Signify Health",
        "parentCompany": "CVS Health (site branded 'part of CVS Healthspire')",
        "category": "Value-based care enablement and managed care-management services",
        "capabilitySummary": "In-home health evaluations, video visits, in-home diagnostics and care coordination pathways, primarily for health plans.",
        "deploymentModel": "managed service",
        "integrationNotes": "No Epic evidence; health-plan-facing.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Current Caravan Health/ACO services for health systems not confirmed on Signify's site as of retrieval"
        ],
        "sources": [
          {
            "title": "Signify Health home page",
            "url": "https://www.signifyhealth.com/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Unite Us",
        "parentCompany": null,
        "category": "Community and social care referral networks",
        "capabilitySummary": "Closed-loop social care referral platform connecting patients to community services (food, housing, behavioral health, financial assistance) with outcome tracking and analytics.",
        "deploymentModel": "SaaS / network service",
        "integrationNotes": "Virtua Health announced plans to embed Unite Us in its Epic EHR (Feb 2025); not yet live at announcement.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Virtua Health partnership (3,000+ providers, 400 locations)"
        ],
        "sources": [
          {
            "title": "Virtua Health to Partner with Unite Us (Business Wire)",
            "url": "https://www.businesswire.com/news/home/20250211794383/en/Virtua-Health-to-Partner-with-Unite-Us-to-Address-Patients-Health-Related-Social-Needs",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "findhelp",
        "parentCompany": null,
        "category": "Community and social care referral networks",
        "capabilitySummary": "Social service search and closed-loop referral platform used by clinicians and patients to connect to community programs.",
        "deploymentModel": "SaaS / network service",
        "integrationNotes": "Integrated into Epic at NYC Health + Hospitals, with patient access via MyChart (Jan 2024).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "NYC Health + Hospitals: 7,000+ programs in network",
          "supports social needs screening of 250,000+ primary care patients in 2023"
        ],
        "sources": [
          {
            "title": "NYC Health + Hospitals integrates new tool into its EMR to connect patients to community resources",
            "url": "https://www.nychealthandhospitals.org/pressrelease/nyc-health-hospitals-integrates-new-tool-into-its-electronic-medical-record-to-connect-patients-to-community-resources/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Salesforce Health Cloud",
        "parentCompany": "Salesforce",
        "category": "Horizontal CRM and cloud platforms",
        "capabilitySummary": "CRM layer around the EHR with integrated care management and care plans, utilization management, contact center, appointment/referral management, home health and Data 360 for Health patient profiles, with Agentforce AI assistance.",
        "deploymentModel": "SaaS",
        "integrationNotes": "FHIR-aligned data model and bidirectional FHIR APIs; Epic not named on the product page.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Customer logos include Nebraska Medicine, Rush, UChicago Medicine, Renown Health, Baptist Health (use cases not specified)"
        ],
        "sources": [
          {
            "title": "Salesforce Health Cloud",
            "url": "https://www.salesforce.com/healthcare-life-sciences/health-cloud/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Microsoft for Healthcare (Dynamics 365, Azure Health Data Services)",
        "parentCompany": "Microsoft",
        "category": "Horizontal CRM and cloud platforms",
        "capabilitySummary": "Capabilities across Dynamics 365, Power Platform, Azure and Microsoft 365 for care management and care plans, patient outreach and contact center, Teams-based care collaboration, virtual visits and remote monitoring on a FHIR-based data service.",
        "deploymentModel": "SaaS / hosted",
        "integrationNotes": "Azure Health Data Services provides a FHIR-based longitudinal record; specific EHR connectors not described on the overview page.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Microsoft and Health Catalyst announced an AI partnership on Azure (HCI, Dec 2025)"
        ],
        "sources": [
          {
            "title": "What is Microsoft for Healthcare? (Microsoft Learn)",
            "url": "https://learn.microsoft.com/industry/healthcare/overview",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Secure Exchange Solutions (SES Notify)",
        "parentCompany": "Centauri Health Solutions (acquired SES, announced Dec 2021)",
        "category": "ADT event notification networks/services",
        "capabilitySummary": "SES Notify sends admit, discharge and critical-lab event alerts to care managers, primary care physicians, specialists and post-acute facilities inside their existing workflow, with configurable triggers and delivery as CCD/XML, PDF or text.",
        "deploymentModel": "network service",
        "integrationNotes": "Vendor states it works with any EHR using standard HL7 ADT messages and needs no on-premise install. No Epic-specific integration was confirmed in sources opened (the open.epic.com reference could not be tied to a specific listing and was dropped).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "KLAS lists Secure Exchange Solutions SES Notify as the 2026 Best in KLAS winner for ADT Notifications.",
          "Centauri's 2021 acquisition release states SES connects to about 90,000 healthcare organizations and 50+ HIEs."
        ],
        "sources": [
          {
            "title": "SES Notify",
            "url": "https://www.secureexsolutions.com/products/ses-notify/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "2026 Best in KLAS Awards: Software and Services",
            "url": "https://klasresearch.com/report/2026-best-in-klas-awards-software-and-services/3906",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Centauri Health Solutions acquires Secure Exchange Solutions (Business Wire)",
            "url": "https://www.businesswire.com/news/home/20211208005227/en/Centauri-Health-Solutions-Adds-Clinical-Data-Exchange-Capabilities-with-Acquisition-of-Secure-Exchange-Solutions",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "ABOUT Healthcare (incl. Ensocare)",
        "parentCompany": "ABOUT Healthcare, Inc. (formerly Central Logic; owner not stated in sources opened)",
        "category": "Post-acute network management / referral placement and discharge orchestration",
        "capabilitySummary": "Patient-flow platform covering inbound transfers, inpatient stay logistics and discharge to post-acute care. Ensocare products (Respond portal for post-acute providers to answer referrals; Choice for patient review of post-acute options before discharge) are now part of the ABOUT family.",
        "deploymentModel": "SaaS",
        "integrationNotes": "No EHR or Epic integration described on the pages opened; confirm with vendor.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "ABOUT states that hundreds of health systems of all sizes use its platform (no customers named).",
          "Central Logic rebrand to ABOUT Healthcare reported October 5, 2021."
        ],
        "sources": [
          {
            "title": "ABOUT Healthcare and Ensocare",
            "url": "https://abouthealthcare.com/ensocare/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "ABOUT Transition for Post-Acute Care",
            "url": "https://abouthealthcare.com/technology/transition-for-post-acute-care-ensocare/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Central Logic integrates services in rebrand to About Healthcare",
            "url": "https://business.dptribune.com/dptribune/news/read/41986644/central_logic_integrates_services_in_rebrand_to_about_healthcare",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "Dina",
        "parentCompany": "Integrated Home Care Services (acquisition announced May 20, 2026)",
        "category": "Post-acute network management / referral placement and discharge orchestration",
        "capabilitySummary": "Builds a digital network of preferred post-acute and in-home providers (home health, home infusion, DME, transportation, personal care, SNF, LTAC) with quality data, partner-responsiveness dashboards, text-based daily bed-availability collection and patient choice lists sent by email or text.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Vendor states connectivity to case management systems and EHRs via HL7 v2, FHIR and API. Epic not named in sources opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Dina's provider page includes a Jefferson Health case study (three hospitals; $570,000 in savings) and a Redeemer Health testimonial.",
          "Integrated Home Care Services announced its acquisition of Dina on May 20, 2026; the combined offer targets Medicaid, Medicare Advantage, commercial and other plan sponsors."
        ],
        "sources": [
          {
            "title": "Dina Transitions of Care Platform for Providers",
            "url": "https://www.dinacare.com/providers",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Integrated Home Care Services acquires Dina",
            "url": "https://www.dinacare.com/integrated-home-care-services-acquires-dina-creating-the-category-defining-platform-for-in-home-benefit-management",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "Olio",
        "parentCompany": null,
        "category": "Post-acute network management / referral placement and discharge orchestration",
        "capabilitySummary": "Care coordination software linking health systems and provider groups with post-acute and behavioral health partners: bi-directional patient status updates, following patients to the next site of care, and network performance views (length of stay, readmissions, post-acute utilization by facility) to steer volume to higher-performing partners.",
        "deploymentModel": "SaaS",
        "integrationNotes": "PointClickCare Marketplace partner; TEFCA record access via Kno2. No Epic integration described in sources opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Deaconess Health System testimonial: coverage of all 44 SNFs in its network, $548,000 estimated savings and an 8.9-day length-of-stay reduction in the first quarter.",
          "Logos shown include Union Health, Deaconess, IU Health, Baptist Health and Oak Street Health (relationships not described)."
        ],
        "sources": [
          {
            "title": "Olio",
            "url": "https://www.olio.health/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Olio for Providers",
            "url": "https://www.olio.health/for-providers",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "Wolters Kluwer UpToDate Patient Engagement (formerly Emmi)",
        "parentCompany": "Wolters Kluwer",
        "category": "Patient outreach / engagement services",
        "capabilitySummary": "Patient education and automated outreach programs aimed at follow-up adherence and fewer readmissions, with reporting on engagement, care-gap analytics, satisfaction and outcome tracking.",
        "deploymentModel": "SaaS",
        "integrationNotes": "No Epic integration described on the page opened; confirm with vendor.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "KLAS lists Wolters Kluwer UpToDate for Patient-Driven Care Management as the 2026 Best in KLAS winner in that segment.",
          "Customer references: UAB Medicine (follow-up appointment adherence, less nurse outreach effort) and Memorial Hospital at Gulfport (lower readmissions and avoidable ED visits)."
        ],
        "sources": [
          {
            "title": "UpToDate Patient Engagement",
            "url": "https://www.wolterskluwer.com/en/solutions/uptodate/enterprise/patient-engagement/driven-by-outcomes",
            "retrieved": "2026-10-21"
          },
          {
            "title": "2026 Best in KLAS Awards: Software and Services",
            "url": "https://klasresearch.com/report/2026-best-in-klas-awards-software-and-services/3906",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "Health Recovery Solutions",
        "parentCompany": null,
        "category": "Remote patient monitoring and post-discharge virtual care",
        "capabilitySummary": "Remote patient monitoring and telehealth (PatientConnect, CareConnect 24/7 monitoring, CaregiverConnect), including logistics from pre-enrollment through post-discharge, hospital-at-home support and EHR-driven enrollment. Acquired Rimidi (announced March 3, 2026) to add diabetes and CGM data capabilities.",
        "deploymentModel": "SaaS",
        "integrationNotes": "HL7 interface with Epic for demographics and vitals; out-of-range alerts to Epic In Basket; vendor states nearly 50 Epic-integrated organizations. Uses Rhapsody engine for HL7, FHIR and API.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Homepage lists MaineHealth (75% reduction in 30-day readmissions for high-risk patients), Hackensack Meridian Health, Penn Medicine and Henry Ford Health.",
          "Novant Health describes its Epic-integrated RPM program on the EHR integration page."
        ],
        "sources": [
          {
            "title": "HRS EHR Integration",
            "url": "https://www.healthrecoverysolutions.com/solutions/ehr-integration",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Health Recovery Solutions",
            "url": "https://www.healthrecoverysolutions.com/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Health Recovery Solutions acquires Rimidi",
            "url": "https://www.healthrecoverysolutions.com/blog/health-recovery-solutions-acquires-rimidi-to-expand-remote-care-programs",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "Vivify Health",
        "parentCompany": "Optum (UnitedHealth Group; acquisition reported Nov 2019)",
        "category": "Remote patient monitoring and post-discharge virtual care",
        "capabilitySummary": "Device-agnostic remote care management platform with personalized care plans, biometric monitoring, video education and an FDA 510(k)-cleared care team portal, used for disease management and post-acute programs.",
        "deploymentModel": "SaaS",
        "integrationNotes": "AVIA lists Epic, Cerner and Allscripts as supported EHRs and acute/ambulatory EMR, patient portal, home health and ADT integrations; AVIA notes EHR integration is use-case dependent.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "AVIA lists 35 verified clients.",
          "Vendor-reported readmission reductions above 65% from IRB studies (not independently verified)."
        ],
        "sources": [
          {
            "title": "AVIA Marketplace: Vivify Pathways +Home",
            "url": "https://marketplace.aviahealth.com/product/24876",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Vivify Health",
            "url": "https://www.vivifyhealth.com/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Optum acquires Vivify Health (HIT Consultant)",
            "url": "https://hitconsultant.net/?p=52279",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "ChatGPT"
        ]
      }
    ],
    "deliveryPath": {
      "lean": "combined",
      "note": "An Epic IDN already licenses much of the core workflow (Healthy Planet, Compass Rose, Case Management, a native readmission model), and Corewell Health shows that a transitions program with large readmission reductions can be built on those modules plus internal reporting. The usual buy decisions are for assets that are hard to build internally: cross-market ADT network coverage (PointClickCare, Bamboo Health, HIEs), post-acute referral networks embedded in Epic (WellSky CarePort via Toolbox), and scaled outreach services. Analytics that rank where to invest across readmission, post-acute and follow-up gaps sit between these and are where third-party analytics vendors, several flagged here as competitors, compete with in-house builds."
    },
    "rfpQuestions": [
      "Which Epic integration points do you use today in production (Toolbox category, Showroom listing, FHIR/HL7 ADT, In Basket/worklist write-back), and can you provide two Epic IDN references using them for transitions of care?",
      "What percentage of hospitals, EDs and SNFs in our primary and secondary service areas are connected to your ADT/referral network today, including non-Epic facilities, and how is coverage measured and reported?",
      "How do you measure the impact on 30-day readmissions and ED revisits, what comparison method do you use (control group, matched cohort, pre/post), and can you share a peer-reviewed or independently audited result?",
      "How are post-discharge follow-up appointment scheduling within 7/14 days and medication reconciliation completion tracked, documented back into Epic, and reported by unit, service line and payer contract?",
      "How does your readmission or utilization risk score compare with Epic's native readmission model on our data, and how is it calibrated, monitored for drift and explained to care managers?",
      "For post-acute partners, what SNF and home health performance measures (readmission rate, length of stay, acceptance time) do you provide to support preferred-network decisions while complying with patient-choice rules?",
      "How are care manager caseloads, outreach attempts and escalations attributed across ACO/shared-savings contracts, and can the data be exported to our enterprise data warehouse without additional fees?",
      "Describe implementation phases, IT staffing we must supply, typical go-live timeframe for an IDN of our size, and the pricing basis (per member, per discharge, per user, per facility)."
    ],
    "sources": [],
    "excluded": [
      {
        "name": "Jvion",
        "reason": "Acquired by Lightbeam Health Solutions in 2022; capabilities now offered within Lightbeam."
      },
      {
        "name": "PatientPing",
        "reason": "Product (Pings) now offered by Bamboo Health; covered under Bamboo Health."
      },
      {
        "name": "Collective Medical / Audacious Inquiry",
        "reason": "Acquired by PointClickCare; covered under PointClickCare."
      },
      {
        "name": "HealthEC / VirtualHealth",
        "reason": "Combined into Elligint Health (Jan 2025); covered under Elligint Health."
      },
      {
        "name": "naviHealth",
        "reason": "Name retired by Optum in 2024; covered as Optum Home & Community Care."
      },
      {
        "name": "Hospital IQ",
        "reason": "Acquired by LeanTaaS (2023); LeanTaaS covered via its Aidin acquisition."
      },
      {
        "name": "Caravan Health",
        "reason": "Reported acquisition by Signify Health (CVS Health); current health-system ACO offering not confirmed on Signify's site."
      },
      {
        "name": "Cardinality.ai",
        "reason": "Serves government health and human services agencies (child welfare, SNAP, TANF); no US health system care-transitions presence found."
      },
      {
        "name": "Medecision (Aerial)",
        "reason": "Only a 2017 customer source located; current health-system presence and ownership could not be verified."
      },
      {
        "name": "Pieces Technologies",
        "reason": "Reported acquisition and current status could not be verified from sources opened."
      },
      {
        "name": "Azara Healthcare",
        "reason": "Ranked #1 in 2025 Best in KLAS Population Health Management, but sources reviewed did not show large-IDN care-transitions deployments; not researched further."
      },
      {
        "name": "NextGen Healthcare Population Health Analytics",
        "reason": "Ranked in 2025 KLAS population health list but ambulatory-EHR-oriented; not relevant to an Epic IDN transitions program."
      },
      {
        "name": "State and regional HIEs (e.g., Manifest MedEx, CRISP)",
        "reason": "Not vendors in the commercial sense and market-specific; noted under the ADT category as an alternative feed to evaluate."
      },
      {
        "name": "Mosai (formerly Forcura and Medalogix)",
        "reason": "Proposed by ChatGPT research; not listed: Real (Forcura + Medalogix merger closed March 19, 2025; rebranded Mosai; Berkshire Partners portfolio company), but it serves home-based care agencies receiving referrals, not the hospital-side transitions program. Relevant only if the IDN operates its own home health or hospice agency."
      }
    ]
  },
  "opp-2": {
    "asOf": "2026-10-21",
    "generation": "pre-generated, reviewed by hand",
    "categories": [
      {
        "name": "Population health / risk stratification analytics",
        "covers": "Multi-source data aggregation (EHR, claims, ADT, SDoH), attribution, registries, risk scoring and stratification, cohort building, care management worklists and contract performance reporting for value-based arrangements.",
        "deploymentModels": [
          "Vendor-hosted SaaS",
          "Cloud data platform with services",
          "Managed analytics services"
        ],
        "fitConsiderations": "For an Epic IDN the main question is what a third-party platform adds over Healthy Planet: typically multi-EHR affiliate data, payer claims normalization, and contract/actuarial analytics. Check write-back into Epic workflows and NCQA measure certification."
      },
      {
        "name": "Care gap and quality measure management (HEDIS, Stars, ACO quality)",
        "covers": "HEDIS/Stars/ACO measure calculation, gap lists, payer gap reconciliation, chart retrieval/abstraction, supplemental data submission and point-of-care gap prompts.",
        "deploymentModels": [
          "Vendor-hosted SaaS",
          "EHR overlay / embedded app",
          "Services (record retrieval, abstraction)"
        ],
        "fitConsiderations": "Several leaders (Cotiviti, Inovalon) sell mainly to health plans; provider-side fit depends on gap reconciliation with each payer and in-workflow delivery in Epic. Cozeva and Reveleer have provider-side Epic evidence."
      },
      {
        "name": "Medication adherence, pharmacy analytics and medication optimization",
        "covers": "Medication adherence measures (PDC for diabetes, RAS antagonists, statins), statin use measures (SPC/SUPD), MTM and comprehensive medication reviews, dispensed-medication history, first-fill tracking, pharmacist-led medication optimization and pharmacy-payer quality programs.",
        "deploymentModels": [
          "SaaS analytics",
          "Network/data service (claims and dispense data)",
          "Clinical pharmacist services",
          "EHR-embedded point-of-care tools"
        ],
        "fitConsiderations": "Adherence measures rely on pharmacy claims or dispense data that health systems do not hold natively; confirm data source (PBM/plan claims vs. Surescripts-type dispense history) and whether pharmacist capacity is in-house or vendor-supplied. Many vendors are health-plan oriented."
      },
      {
        "name": "EHR-native population health modules",
        "covers": "Registries, care gap prompts, risk scores, care management, payer claims ingestion and quality submission built into the EHR.",
        "deploymentModels": [
          "EHR module (hosted with EHR)",
          "Vendor cloud (Oracle Cloud Infrastructure)"
        ],
        "fitConsiderations": "For an Epic client, Healthy Planet is the default baseline and any third-party purchase is judged against it. Oracle Health is relevant mainly as an EHR-agnostic option or for Oracle-based affiliates."
      },
      {
        "name": "Clinical AI / risk model vendors",
        "covers": "Machine-learning risk models (avoidable admission, disease progression, rising risk), AI assistants that surface gaps and suspect conditions at the point of care.",
        "deploymentModels": [
          "SaaS model platform",
          "Embedded in EHR workflow",
          "Model build platform for in-house data science"
        ],
        "fitConsiderations": "Validation on the client's own population, bias monitoring, explainability and the path from score to worklist matter more than model accuracy claims. Epic's own models and Cosmos-based models set the in-house baseline."
      },
      {
        "name": "Patient outreach for gap closure",
        "covers": "Automated SMS/voice/email/portal campaigns, scheduling and self-scheduling, AI agents that chart-scrub and book gap-closing visits, CRM-based care coordination.",
        "deploymentModels": [
          "SaaS with EHR scheduling integration",
          "AI agent platform",
          "CRM platform"
        ],
        "fitConsiderations": "Check direct booking into Epic Cadence, use of EHR communication preferences/opt-outs, overlap with MyChart and Epic campaign tools, and per-message or per-patient pricing."
      },
      {
        "name": "Value-based care enablement partners (risk-sharing operating partners)",
        "covers": "Organizations that pair technology with operating support and share financial risk/savings in MSSP, MA or commercial contracts.",
        "deploymentModels": [
          "Partnership with shared savings / risk-sharing",
          "Technology plus services"
        ],
        "fitConsiderations": "Relevant when the IDN wants an operating partner for ACO performance rather than software only; economics are tied to savings, which changes the buy decision."
      },
      {
        "name": "Healthcare data, benchmarking and market/performance intelligence",
        "covers": "Claims-based benchmarking, provider performance, market and network analytics, actuarial/contract modeling used to target and size interventions.",
        "deploymentModels": [
          "SaaS analytics",
          "Data platform / data products (including in customer Databricks)"
        ],
        "fitConsiderations": "Adjacent to gap closure: useful for sizing opportunity and contract modeling, not for patient-level outreach. Several overlap with analytics-prioritization products and are flagged as competitors."
      }
    ],
    "vendors": [
      {
        "name": "Arcadia",
        "parentCompany": "Arcadia Solutions (majority stake acquired by Nordic Capital, announced July 2025; previously Peloton Equity)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "Healthcare data platform that aggregates clinical, claims, social and engagement data for risk stratification, care management prioritization (Ascent Care), quality gap tracking and value-based contract performance; acquired CareJourney (claims-based market and network analytics).",
        "deploymentModel": "Vendor-hosted platform; ISO 27001, SOC 2 and HITRUST certified per vendor site (hosting model not stated on pages opened).",
        "integrationNotes": "No Epic-specific integration described on pages opened. EHR embedding precedent: MEDITECH built Arcadia risk scores, attribution and care gaps into Expanse care management workflows (2019).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Best in KLAS 2025 Population Health Management: Arcadia Analytics ranked 5th, score 79.7",
          "KLAS Value-Based Care Reimbursement 2022: top-ranked with Epic and Innovaccer; cited for closing quality gaps and combined clinical/financial reporting",
          "NCQA HEDIS and AMP measure certifications listed on vendor site"
        ],
        "sources": [
          {
            "title": "Arcadia population health (Ascent Care)",
            "url": "https://www.arcadia.io/population-health",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Nordic Capital acquires majority stake in Arcadia (MobiHealthNews)",
            "url": "https://www.mobihealthnews.com/news/nordic-capital-acquires-majority-stake-arcadia",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Best in KLAS 2025: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          },
          {
            "title": "KLAS, Epic, Arcadia, Innovaccer Earn Top Marks for Value-Based Care (TechTarget, Nov 2022)",
            "url": "https://www.techtarget.com/revcyclemanagement/news/366600642/KLAS-Epic-Arcadia-Innovaccer-Earn-Top-Marks-for-Value-Based-Care",
            "retrieved": "2026-10-21"
          },
          {
            "title": "MEDITECH continues to optimize Arcadia.io collaboration with Expanse Population Health (BioSpace)",
            "url": "https://www.biospace.com/meditech-continues-to-optimize-arcadia-io-collaboration-with-expanse-population-health",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Innovaccer",
        "parentCompany": "Innovaccer Inc. (independent, venture-backed; Series F Jan 2025)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "Data and AI platform (Gravity) with population health analytics, AI risk stratification on clinical, claims and SDoH data, quality measure status (numerator/denominator/exclusion) and care-gap tracking, care management and point-of-care gap delivery. Acquired Cured (CRM/outreach, Jan 2024), Pharmacy Quality Solutions (Mar 2024), Humbi AI (actuarial, Jan 2025) and Story Health (Sep 2025).",
        "deploymentModel": "Vendor-hosted cloud SaaS (Gravity described as cloud-agnostic).",
        "integrationNotes": "Vendor states it integrates with major EHRs and delivers gaps inside clinical workflows, and that large Epic, Oracle Cerner and MEDITECH customers use it; no Epic-specific integration mechanics described. Banner Health (Oracle Cerner) deployment shows EHR in-office gap reminders.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Best in KLAS 2025 Population Health Management: Innovaccer Population Health ranked 3rd, score 85.7",
          "Banner Health: ~1.4M value-based lives, care-gap flags shown in EHR, ~18% gap closure among providers using in-office workflow (2022)",
          "KLAS Value-Based Care Reimbursement 2022: top-ranked; cited for dashboards and custom builds"
        ],
        "sources": [
          {
            "title": "Innovaccer Population Health Analytics",
            "url": "https://webflow.innovaccer.com/products/population-health-analytics",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Banner Health's big bet on population health (Becker's)",
            "url": "https://www.beckershospitalreview.com/innovation/banner-health-s-big-bet-on-population-health.html",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Innovaccer (Wikipedia)",
            "url": "https://en.wikipedia.org/wiki/Innovaccer",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Best in KLAS 2025: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Evidence source",
            "url": "https://www.techtarget.com/revcyclemanagement/news/366600642/KLAS-Epic-Arcadia-Innovaccer-Earn-Top-Marks-for-Value-Based-Care",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Health Catalyst",
        "parentCompany": "Health Catalyst, Inc. (Nasdaq: HCAT)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "Population health applications on its Ignite data platform: explainable clinical, utilization and cost risk models, Population Builder cohorting (21 chronic registries, 6,000+ value sets, ML risk models), automated CMS/HEDIS measure tracking, outreach prioritization and ACO/BPCI contract tracking. Acquired Upfront Healthcare Services (patient engagement) in 2025.",
        "deploymentModel": "Cloud data platform (Ignite) plus applications and services; three-phase implementation described by vendor.",
        "integrationNotes": "Pages opened describe EMR and claims data ingestion but do not describe Epic-specific integration.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Best in KLAS 2025 Population Health Management: Health Catalyst Population Health Applications listed (not ranked), score 80.6",
          "Vendor success stories reference PeaceHealth and Lehigh Valley Health Network",
          "UnityPoint Health migrating to Ignite Data and Analytics; CEO transition announced (Dan Burton to step down June 30, 2026)"
        ],
        "sources": [
          {
            "title": "Health Catalyst Population Health Management",
            "url": "https://www.healthcatalyst.com/healthcare-analytics/population-health-value-based-care/population-health-management",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Population Builder Stratification Module",
            "url": "https://www.healthcatalyst.com/insights/population-builder-stratification-module",
            "retrieved": "2026-10-21"
          },
          {
            "title": "New Leadership, Partnerships, and Platforms Mark Health Catalyst's Year (HCI, Dec 2025)",
            "url": "https://www.hcinnovationgroup.com/finance-revenue-cycle/article/55336749/new-leadership-partnerships-and-platforms-mark-health-catalysts-transformative-year",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Best in KLAS 2025: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Lightbeam Health Solutions",
        "parentCompany": "Lightbeam Health Solutions (ownership not stated in sources opened)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "Population health platform for ACOs, payers and providers with AI risk stratification (including Jvion's CORE clinical AI and SDoH assets acquired 2022), quality and risk adjustment optimization, rising-risk/CCM/TCM programs, deviceless RPM across 30+ condition programs (CHF, COPD, diabetes) and point-of-care integration; acquired Syntax Health (value-based contract modeling) Dec 2025.",
        "deploymentModel": "Vendor-hosted platform plus clinical and financial advisory services (hosting model not stated).",
        "integrationNotes": "Vendor lists point-of-care integration; no Epic-specific evidence in sources opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Best in KLAS 2025 Population Health Management: ranked 4th, score 82.7",
          "Best in KLAS 2025 Remote Patient Monitoring (93.6)",
          "Vendor states it is AMGA's exclusive partner for analytics and population health management"
        ],
        "sources": [
          {
            "title": "Lightbeam Health Awarded Best in KLAS for Remote Patient Monitoring",
            "url": "https://lightbeamhealth.com/news/lightbeam-health-awarded-best-in-klas-for-remote-patient-monitoring/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Lightbeam acquires Jvion's AI and SDOH assets (Healthcare IT News)",
            "url": "https://www.healthcareitnews.com/news/lightbeam-acquires-jvions-ai-and-sdoh-assets",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Lightbeam Health Solutions Acquires Syntax Health",
            "url": "https://lightbeamhealth.com/news/lightbeam-health-solutions-acquires-syntax-health-expanding-value-based-contracting-and-incentive-design-capabilities/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Best in KLAS 2025: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Optum Performance Analytics",
        "parentCompany": "Optum (UnitedHealth Group)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "Enterprise claims and clinical analytics with identification and stratification (ID Strat), validated cost risk models, rising-risk (ED/inpatient) identification, care gap and clinical-standard noncompliance detection, HEDIS/Stars support and provider scorecards.",
        "deploymentModel": "Vendor-hosted analytics platform (deployment model not stated in sell sheet).",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Vendor-cited recognition: Frost & Sullivan PHM Company of the Year (2021), IDC MarketScape Leader in U.S. Population Health Analytics (2022)"
        ],
        "sources": [
          {
            "title": "Optum Performance Analytics sell sheet",
            "url": "https://cdn-aem.optum.com/content/dam/optum3/optum/en/resources/sell-sheet/optum-performance-analytics-sell-sheet.pdf",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Optum earns acclaim from Frost & Sullivan for leading the PHM market (2018)",
            "url": "https://www.frost.com/news/press-releases/optum-earns-acclaim-from-frost-sullivan-for-leading-the-population-health-management-market-with-its-integrated-technology-platforms-and-services/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Cedar Gate Technologies",
        "parentCompany": "Cedar Gate Technologies (ownership not stated in sources opened)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "Single platform combining value-based care analytics, benefits analytics, care management, clinical decision support, utilization management and payment (bundles) technology for payers, providers and employers.",
        "deploymentModel": "Vendor platform (SaaS model not explicitly stated in source).",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Highest innovation index score in Frost Radar: U.S. Population Health Management 2024 (per vendor release, Jan 2025)"
        ],
        "sources": [
          {
            "title": "Cedar Gate Technologies Named Innovation Leader in the U.S. Population Health Management Market",
            "url": "https://www.businesswire.com/news/home/20250107462885/en/Cedar-Gate-Technologies-Named-Innovation-Leader-in-the-U.S.-Population-Health-Management-Market",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Elligint Health (HealthEC + VirtualHealth)",
        "parentCompany": "Elligint Health (formed 2024 from HealthEC and VirtualHealth; backers not disclosed)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "Combines HealthEC population health analytics with VirtualHealth HELIOS care/utilization management: data-agnostic, FHIR-native aggregation, risk stratification of high-risk populations, configurable care management workflows and value-based performance tools.",
        "deploymentModel": "Vendor platform (hosting model not stated).",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Best in KLAS 2025 PHM: Elligint Health HealthEC Population Health Management Suite listed (not ranked), score 71.1"
        ],
        "sources": [
          {
            "title": "HealthEC and VirtualHealth Unite to Launch Elligint Health (Business Wire, Jan 2025)",
            "url": "https://www.businesswire.com/news/home/20250109141307/en/HealthEC-and-VirtualHealth-Unite-to-Launch-Elligint-Health-Redefining-the-Future-of-Healthcare-with-Intelligent-Solutions",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Best in KLAS 2025: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Persivia",
        "parentCompany": "Persivia (ownership not stated in sources opened)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "CareSpace modular digital health platform with the Soliton AI engine: multi-source data aggregation, contract-aware workflows, patient prioritization for care teams, risk adjustment automation and quality management.",
        "deploymentModel": "Vendor platform (hosting model not stated).",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Targets ACOs and value-based organizations; no named customers on page opened"
        ],
        "sources": [
          {
            "title": "Persivia at APG 2025",
            "url": "https://wp.persivia.com/blog/apg-2025/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "low",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Azara Healthcare (DRVS)",
        "parentCompany": "Azara Healthcare (merged with i2i Population Health Feb 2025; Insight Partners led investment; Hughes & Company existing investor)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "DRVS quality measurement, analytics and reporting platform that unifies clinical, health plan, HIE and SDoH data to close care gaps and support compliance reporting, focused on community health centers, rural and community hospitals.",
        "deploymentModel": "Vendor-hosted SaaS (per market positioning; not explicitly stated in source).",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Best in KLAS 2025 Population Health Management winner, score 92.8"
        ],
        "sources": [
          {
            "title": "Azara Healthcare and i2i Population Health Unite (Insight Partners)",
            "url": "https://www.insightpartners.com/ideas/azara-healthcare-and-i2i-population-health-unite-to-supercharge-safety-net-population-health-management/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Best in KLAS 2025: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "ZeOmega (Jiva)",
        "parentCompany": "ZeOmega (privately held)",
        "category": "Population health / risk stratification analytics",
        "capabilitySummary": "Jiva population health management and care management platform with clinical content, rules engine and integration tooling; also utilization management and prior authorization. Primarily serves health plans and risk-bearing organizations, with stated expansion into payvider organizations.",
        "deploymentModel": "Vendor platform (hosting model not stated).",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "#1 Best in KLAS 2025 Payer Care Management (fourth consecutive year)"
        ],
        "sources": [
          {
            "title": "ZeOmega named #1 Best in KLAS for Payer Care Management (MAHP repost, Feb 2025)",
            "url": "https://www.mahp.org/2025/02/14/zeomega-named-1-best-in-klas-for-payer-care-management-solutions-for-fourth-consecutive-year/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Cozeva",
        "parentCompany": "Applied Research Works, Inc.",
        "category": "Care gap and quality measure management (HEDIS, Stars, ACO quality)",
        "capabilitySummary": "Cloud-based HEDIS, AMP and risk adjustment platform with a real-time metrics engine, payer care-gap reconciliation, pre-visit and point-of-care gap flags, provider dashboards and submission services; NCQA, DirectTrust, EHNAC and ONC certifications per vendor.",
        "deploymentModel": "Cloud-based SaaS with EHR overlay for affiliated practices.",
        "integrationNotes": "Providence (California) medical group runs Cozeva integrated with Epic; an overlay covers affiliated providers on other EHRs (Healthcare IT News, July 2025). Also listed in athenahealth Marketplace.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Providence: quality contracts grew from 1 to 50+ and covered patients from ~100,000 to 650,000+ (2016-2024); 50+ quality awards in 2024",
          "Best in KLAS 2025 PHM: Cozeva listed (not ranked), score 87.4"
        ],
        "sources": [
          {
            "title": "Providence scores big VBC wins with risk and quality reporting tool (Healthcare IT News)",
            "url": "https://www.healthcareitnews.com/news/providence-scores-big-vbc-wins-risk-and-quality-reporting-tool",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Cozeva partners with athenahealth's Marketplace Program",
            "url": "https://corp.cozeva.com/cozevapartnership",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Best in KLAS 2025: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Cotiviti (Quality Intelligence, Star Intelligence, DxCG)",
        "parentCompany": "Cotiviti (co-owned equally by Veritas Capital and KKR since May 2024; acquired Edifecs March 2025)",
        "category": "Care gap and quality measure management (HEDIS, Stars, ACO quality)",
        "capabilitySummary": "NCQA-certified HEDIS and quality measure engine, Medicare Stars modeling, population risk/cost stratification (Medical Intelligence, DxCG models), member-level gap analysis, medical record retrieval and abstraction; Edifecs adds data management and interoperability.",
        "deploymentModel": "Vendor-hosted platform and services (deployment model not stated on page opened).",
        "integrationNotes": "Vendor describes direct EMR extraction for record retrieval; no Epic-specific evidence in sources opened. Sold primarily to health plans; providers positioned as network collaborators.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Vendor: 129M+ members reported to NCQA processed by Quality Intelligence; 44% of 5-star contracts work with Cotiviti quality products",
          "25th consecutive year of NCQA HEDIS measure certification (July 2025)"
        ],
        "sources": [
          {
            "title": "Cotiviti Quality and Stars",
            "url": "https://www.cotiviti.com/solutions/quality-and-stars",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Cotiviti Completes Recapitalization with KKR and Long-Standing Owner Veritas",
            "url": "https://www.veritascapital.com/cotiviti-completes-recapitalization-with-kkr-and-long-standing-owner-veritas/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Cotiviti Completes Acquisition of Edifecs",
            "url": "https://www.cotiviti.com/press-release/cotiviti-completes-acquisition-of-edifecs",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Evidence source",
            "url": "https://www.businesswire.com/news/home/20250731132324/en/Cotivitis-Quality-Measurement-and-Reporting-Solution-Achieves-25th-Consecutive-Year-of-HEDIS-Measure-Certification",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Inovalon (Converged Quality)",
        "parentCompany": "Inovalon (taken private by a Nordic Capital-led consortium incl. Insight Partners, announced Aug 2021)",
        "category": "Care gap and quality measure management (HEDIS, Stars, ACO quality)",
        "capabilitySummary": "NCQA-certified HEDIS quality measurement with add-ons for benchmarking, Star Ratings forecasting, medical record abstraction, population outcomes analytics and provider/member engagement; integrates with Converged Risk.",
        "deploymentModel": "Cloud-based SaaS (payer cloud).",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "25th consecutive NCQA HEDIS Measure Certification; used by 100+ health plans; Cambia Health Solutions quoted (July 2025)"
        ],
        "sources": [
          {
            "title": "Inovalon's Converged Quality Achieves 25th Consecutive NCQA HEDIS Measure Certification",
            "url": "https://www.businesswire.com/news/home/20250731417212/en/Inovalons-Converged-Quality-Achieves-25th-Consecutive-NCQA-HEDIS-Measure-Certification",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Nordic Capital, Insight Partners take Inovalon private (Fierce Healthcare)",
            "url": "https://www.fiercehealthcare.com/tech/nordic-capital-insight-partners-take-inovalon-private-6-4b-acquisition",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Reveleer",
        "parentCompany": "Reveleer (ownership not stated in sources opened; acquired Novillus April 2025)",
        "category": "Care gap and quality measure management (HEDIS, Stars, ACO quality)",
        "capabilitySummary": "Provider-side quality improvement, AI care gap identification, prospective and retrospective risk adjustment and HEDIS audit support, delivering visit-aligned gaps and suspect conditions in the clinician workflow; Novillus adds care gap management and provider engagement.",
        "deploymentModel": "Vendor-hosted SaaS plus services.",
        "integrationNotes": "Vendor states native integration with Epic, athenaOne and other EHRs, with gaps shown in workflow without extra logins.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Vendor case: Florida health system increased HCC capture per patient 55%",
          "Combined company serves 72M covered lives across 86 organizations (April 2025)"
        ],
        "sources": [
          {
            "title": "Reveleer Healthcare Provider Solutions",
            "url": "https://www.reveleer.com/who-we-serve/provider-solutions",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Reveleer acquires Novillus (DotMed)",
            "url": "https://www.dotmed.com/legal/print/story.html?nid=64684",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Vim",
        "parentCompany": "Vim (ownership not stated in sources opened)",
        "category": "Care gap and quality measure management (HEDIS, Stars, ACO quality)",
        "capabilitySummary": "Point-of-care connectivity layer that delivers payer quality gap data into provider EHRs with read-write capability so care teams act on gaps in workflow; partners include Availity and Veradigm.",
        "deploymentModel": "EHR-connected SaaS application layer.",
        "integrationNotes": "Page opened does not name supported EHRs; Epic support not verified.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Availity and Vim partnership to streamline care gap notification and closure (2025)"
        ],
        "sources": [
          {
            "title": "Vim Quality Gaps",
            "url": "https://getvim.com/solution/quality-gaps/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Evidence source",
            "url": "https://getvim.com/news-and-events/availity-and-vim-partner-to-streamline-care-gap-notification-and-closure-process/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Arine",
        "parentCompany": "Arine (independent; SCAN Group strategic investor, 2021)",
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "AI and clinician-driven medication management platform that identifies medication-related problems and recommends interventions: high-risk member programs (LUMINATE), prescriber analytics (RESONATE), quality/Stars improvement (ELEVATE) and MTM.",
        "deploymentModel": "SaaS platform with clinical services.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "45+ health plan clients incl. two national PBMs; SCAN testimonial on antihypertensive (92%) and statin adherence",
          "VNS Health case study: 5 stars on adherence measures; 70% of care gaps closed within six months (vendor-reported)"
        ],
        "sources": [
          {
            "title": "Arine home page",
            "url": "https://arine.io/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Arine VNS Health case study",
            "url": "https://go.arine.io/vnsny-case-study",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Pharmacy Quality Solutions (EQuIPP)",
        "parentCompany": "Innovaccer (acquired March 2024)",
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "Neutral pharmacy-payer quality platform (EQuIPP) that standardizes medication-use quality measures (adherence, outcomes, safety), benchmarks pharmacy performance, surfaces gaps and coordinates value-based pharmacy payments.",
        "deploymentModel": "Network SaaS platform connecting plans and pharmacies.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Covers 95% of community pharmacies and up to 60M lives; partners with 5 of top 6 MA plans (at acquisition)",
          "65,000+ pharmacy NPIs connected (Drug Topics, Oct 2026)"
        ],
        "sources": [
          {
            "title": "Innovaccer Announces Acquisition of Pharmacy Quality Solutions",
            "url": "https://www.businesswire.com/news/home/20240312730351/en/Innovaccer-Announces-Acquisition-of-Pharmacy-Quality-Solutions-to-Accelerate-Value-Based-Care-in-Pharmacy-Settings",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Patient First: How EQuIPP Connects Pharmacies and Payers (Drug Topics)",
            "url": "https://www.drugtopics.com/view/patient-first-how-equipp-connects-pharmacies-and-payers-to-drive-better-health-outcomes",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Surescripts (Medication History for Populations)",
        "parentCompany": "Surescripts (ownership not described in source opened)",
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "National health information network; Medication History for Populations gives care teams dispensed-medication data to assess adherence, reconcile medications, identify higher-risk patients and support chronic-condition gap outreach.",
        "deploymentModel": "Network data service delivered to care management and analytics systems.",
        "integrationNotes": "Release opened does not describe EHR integration for this product.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Baptist Health Louisville received patient medication data in 1-3 days vs. about a month (Feb 2025)"
        ],
        "sources": [
          {
            "title": "Providing Care Managers with Patient Medication History Data Could Help Increase Adherence (Business Wire, Feb 2025)",
            "url": "https://www.businesswire.com/news/home/20250219497477/en/Providing-Care-Managers-with-Patient-Medication-History-Data-Could-Help-Increase-Adherence-and-Save-U.S.-Healthcare-3.76-Billion",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "DrFirst",
        "parentCompany": "DrFirst (ownership not verified in sources opened)",
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "Medication history (MedHx), real-time prescription benefit and medication management tools, including population-level medication history used to measure first-fill and adherence.",
        "deploymentModel": "SaaS / network service embedded in EHR workflow.",
        "integrationNotes": "Temple University Health System integrating DrFirst AI medication history and benefit-check tools into Epic (2023). University of Maryland Medical System merged DrFirst medication data with Epic records to compute first-fill rates.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "UMMS: prescription first-fill rates up 20-25%, adherence up 8-14% (vendor case study, undated)"
        ],
        "sources": [
          {
            "title": "Temple Health: DrFirst AI tools integrated into Epic (Becker's mention)",
            "url": "https://templehealth.org/about/news/in-the-media/beckers-health-it-highlights-temple-university-health-system-integrating-ai-powered-tools-from-drfirst-into-epic-electronic-health-record-system",
            "retrieved": "2026-10-21"
          },
          {
            "title": "UMMS boosts prescription first-fill rates with DrFirst (case study)",
            "url": "https://www.casestudies.com/company/drfirst/case-study/university-of-maryland-medical-system-boosts-prescription-first-fill-rates-20-to-25-with-drfirst",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "OptimizeRx",
        "parentCompany": "OptimizeRx Corp. (Nasdaq: OPRX)",
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "Point-of-care messaging network that delivers patient savings and adherence-support information for medications inside EHR prescribing workflows; commercial model is oriented to pharmaceutical manufacturers.",
        "deploymentModel": "EHR-embedded messaging network.",
        "integrationNotes": "Announced technical integration with Epic and Cerner for hospital/health system prescribers (April 2019); current scope not verified.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Hospital and health system expansion announcement with Epic/Cerner integration (2019)"
        ],
        "sources": [
          {
            "title": "OptimizeRx Expands Investment in Hospitals and Health Systems",
            "url": "https://investors.optimizerx.com/news-releases/news-release-details/optimizerx-expands-investment-hospitals-and-health-systems",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Cardinal Health Outcomes",
        "parentCompany": "Cardinal Health",
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "Pharmacy-based clinical services network combining OutcomesMTM (pharmacist MTM), mscripts (refill and reminder app) and TelePharm, connecting payers and pharma-sponsored adherence and quality programs to community pharmacists.",
        "deploymentModel": "Network platform for pharmacies, payers and manufacturers.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Integrated into workflow of 18,000+ pharmacies via TDS (Nov 2022)"
        ],
        "sources": [
          {
            "title": "Cardinal Health announces new platform to improve drug adherence (Healthcare IT News, 2021)",
            "url": "https://www.healthcareitnews.com/news/cardinal-health-announces-new-platform-improve-drug-adherence",
            "retrieved": "2026-10-21"
          },
          {
            "title": "TDS adds Cardinal Health's Outcomes platform",
            "url": "https://www.outcomes.com/press/tds-adds-cardinal-healths-outcomes-platform",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "AssureCare (AssureRx, incl. Cureatr and SinfoníaRx technology)",
        "parentCompany": "AssureCare",
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "AssureRx medication management portfolio expanded in June 2024 with technology acquired from Cureatr (Meds 360 medication management) and SinfoníaRx (value-based MTM services and RxCompanion claims-based medication-problem flagging), for MTM, reconciliation and adherence.",
        "deploymentModel": "Software plus pharmacist-led MTM services.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Lineage: Cureatr acquired SinfoníaRx from Tabula Rasa HealthCare (March 2023); AssureCare acquired Cureatr and SinfoníaRx technology (June 2024)"
        ],
        "sources": [
          {
            "title": "AssureCare acquires Cureatr and SinfoníaRx technology (June 2024)",
            "url": "https://www.santelog.com/actualites-sante-nasdaq/assurecare-acquires-cureatr-and-sinfoniarx-technology",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Cureatr acquires SinfoníaRx from Tabula Rasa HealthCare (March 2023)",
            "url": "https://stocktitan.net/news/TRHC/medication-management-services-leader-cureatr-acquires-sinfonia-mmctab44ob0n.html",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "RxAnte (RxEffect, Mosaic Pharmacy Service)",
        "parentCompany": "RxAnte (Primus Capital growth investment; full ownership not verified)",
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "RxEffect adherence workflow tool deployed by health plans to plan staff and affiliated provider practices, plus Mosaic Pharmacy Service offerings for plans, providers and patients.",
        "deploymentModel": "SaaS workflow with pharmacy services; HITRUST and SOC 2 listed.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Health plans extend RxEffect to affiliated providers (vendor page, no figures)"
        ],
        "sources": [
          {
            "title": "RxAnte for value-based providers",
            "url": "https://rxante.com/providers",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Linea",
        "parentCompany": "Linea (backed by Redesign Health and DaVita)",
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "Post-acute heart failure care service combining AI admission/discharge detection with pharmacist titration of guideline-directed medical therapy, nurse risk assessments and 90 days of SMS support for ACOs and risk-bearing providers.",
        "deploymentModel": "Tech-enabled clinical service (software plus nurse/pharmacist team).",
        "integrationNotes": "Article states it integrates into existing clinical workflows; no named EHR.",
        "typicalTimeToDeploy": null,
        "typicalCost": "No cost to patients; ACOs pay a value-based fee only for actively enrolled patients (no amount published).",
        "evidence": [
          "Company-reported: 50% reduction in readmissions for ACOs; 90-day readmission rate under 25% (Aug 2025)"
        ],
        "sources": [
          {
            "title": "Linea, backed by Redesign Health and DaVita, takes AI-driven approach to cut HF readmissions (Fierce Healthcare)",
            "url": "https://www.fiercehealthcare.com/ai-and-machine-learning/startup-linea-backed-redesign-health-and-davita-takes-ai-driven-approach",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Epic Healthy Planet (with Cosmos-based AI)",
        "parentCompany": "Epic Systems Corporation",
        "category": "EHR-native population health modules",
        "capabilitySummary": "Epic's population health and accountable care module: risk stratification, registries, point-of-care gap prompts, multichannel gap-closure campaigns, payer claims integration, QRDA aggregation/submission and contract metric tracking. Epic has previewed Cosmos-trained generative models (Cosmos AI / Curiosity) for outcome and risk forecasting, with no general release date stated.",
        "deploymentModel": "EHR module within the client's Epic instance (on-premises or Epic-hosted).",
        "integrationNotes": "Native to Epic; claims and external data ingested into the Epic record.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Best in KLAS 2025 Population Health Management: ranked 2nd, score 89.2",
          "Essentia Health uses Healthy Planet as care coordination platform (Epic page)",
          "Novant Health tracks measures incl. A1C control, BP and statin therapy for CVD in Healthy Planet (2017)",
          "Cosmos AI previewed at UGM Aug 2025; evaluated on 78 tasks; no release date"
        ],
        "sources": [
          {
            "title": "Epic Population Health",
            "url": "https://www.epic.com/software/population-health/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Novant Health deploys Epic's Healthy Planet (Healthcare IT News)",
            "url": "https://healthcareitnews.com/news/novant-health-deploys-epics-healthy-planet-tech-track-patient-conditions",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Epic unveils AI features, Cosmos AI risk prediction preview (Fierce Healthcare, Aug 2025)",
            "url": "https://www.fiercehealthcare.com/health-tech/epic-unveils-major-ai-features-ai-charting-microsoft-cosmos-ai-risk-prediction-and-rcm",
            "retrieved": "2026-10-21"
          },
          {
            "title": "The Rise of AI-Powered Health Prediction, Featuring Epic's Curiosity (Epic, Mar 2026)",
            "url": "https://www.epic.com/epic/post/the-rise-of-ai-powered-health-prediction-featuring-epics-curiosity/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Best in KLAS 2025: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Oracle Health (Health Data Intelligence, Quality Management, Care Coordination Intelligence)",
        "parentCompany": "Oracle Corporation",
        "category": "EHR-native population health modules",
        "capabilitySummary": "Health Data Intelligence (formerly HealtheIntent) aggregates and normalizes clinical, financial and third-party data; Quality Management uses AI-assisted tools to identify care gaps, hierarchical conditions and readmission risk; Care Coordination Intelligence supports outreach and gap closure; prebuilt chronic condition analytics.",
        "deploymentModel": "Fully managed suite hosted on Oracle Cloud Infrastructure.",
        "integrationNotes": "Vendor describes the platform as EHR-agnostic and Quality Management as working in any EHR; no Epic-specific deployment evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Expanded clinical quality content and EHR care-gap alerts announced March 2024"
        ],
        "sources": [
          {
            "title": "Oracle Health condition management",
            "url": "https://oracle.com/health/population-health/condition-management",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Oracle expands healthcare platform (Becker's, March 2024)",
            "url": "https://www.beckershospitalreview.com/ehrs/oracle-expands-healthcare-platform.html",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "ClosedLoop",
        "parentCompany": "ClosedLoop.ai (independent; status 'alive' per CB Insights)",
        "category": "Clinical AI / risk model vendors",
        "capabilitySummary": "Healthcare data science platform used by providers, payers and digital health companies to build and run machine-learning risk models, including rising-risk identification.",
        "deploymentModel": "SaaS model-building and scoring platform.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Named in May 2025 Black Book release as a platform for rising-risk populations (per CB Insights profile)"
        ],
        "sources": [
          {
            "title": "ClosedLoop company profile (CB Insights)",
            "url": "https://www.cbinsights.com/company/closedloopai",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Medial EarlySign",
        "parentCompany": "Medial EarlySign (Israel-founded, 2013)",
        "category": "Clinical AI / risk model vendors",
        "capabilitySummary": "Machine-learning models on routine EHR data to flag patients at risk of conditions such as diabetes progression, diabetic kidney disease and hypertension for population health teams.",
        "deploymentModel": "Software models integrated with provider EHR data.",
        "integrationNotes": "KLAS spotlight reports long integration timelines and significant clinician time for implementation.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "KLAS Emerging Technology spotlight: customers satisfied, all would buy again; integration takes a long time (undated)",
          "Model identified 45% of diabetic patients who developed significant kidney damage within a year by flagging under 5% of cohort (2018)"
        ],
        "sources": [
          {
            "title": "Medial EarlySign eTech Spotlight (KLAS)",
            "url": "https://klasresearch.com/blog/medial-earlysign-etech-spotlight/707",
            "retrieved": "2026-10-21"
          },
          {
            "title": "AI mines EHR data to predict diabetic patients at risk for kidney damage (Healthcare IT News, 2018)",
            "url": "https://www.healthcareitnews.com/news/ai-mines-ehr-data-predict-diabetic-patients-risk-kidney-damage-study-finds",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Navina",
        "parentCompany": "Navina (independent; $55M Series C led by Goldman Sachs Alternatives, March 2025)",
        "category": "Clinical AI / risk model vendors",
        "capabilitySummary": "AI assistant for primary care that aggregates structured and unstructured data into pre-visit patient profiles to support documentation of condition severity, risk scoring and value-based care work at the point of care.",
        "deploymentModel": "SaaS assistant alongside EHR.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Customers include agilon health (2,800 PCPs), Privia Health, InnovaCare, Millennium Physician Group, Holzer Health System; 2025 Best in KLAS for clinician digital workflow"
        ],
        "sources": [
          {
            "title": "Navina reels in $55M Series C led by Goldman Sachs (Fierce Healthcare)",
            "url": "https://www.fiercehealthcare.com/ai-and-machine-learning/navina-reels-55m-series-c-led-goldman-sachs",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Notable",
        "parentCompany": "Notable (ownership not stated in source opened)",
        "category": "Patient outreach for gap closure",
        "capabilitySummary": "AI agents that scrub charts for open gaps, verify gap status, run multilingual SMS/email/voice outreach, book appointments, prepare pre-visit notes and write results back to the EHR.",
        "deploymentModel": "SaaS AI agent platform integrated with the EHR.",
        "integrationNotes": "Writes back to 'the EHR' per vendor; Epic not named on page opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": "Vendor-reported Montefiore example: $60,000 of product utilization against $400,000 ROI (not a list price).",
        "evidence": [
          "Montefiore: 50,000+ charts reviewed, gap closure rate from 3-4% to 7%, 4,000+ incremental closures (vendor blog, May 2026)"
        ],
        "sources": [
          {
            "title": "How to close care gaps without hiring a team of 10 (Notable)",
            "url": "https://www.notablehealth.com/blog/how-to-close-care-gaps-without-hiring-a-team-of-10",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Luma Health",
        "parentCompany": "Luma Health (independent; ownership not stated)",
        "category": "Patient outreach for gap closure",
        "capabilitySummary": "Patient engagement and access platform with the Navigator AI voice/SMS agent for preventive-service outreach (e.g., diabetic eye exams, wellness visits), rebooking and referral scheduling, plus fax and workflow automation.",
        "deploymentModel": "SaaS with EHR scheduling integration.",
        "integrationNotes": "Vendor positions as an Epic add-on integrating with In Basket, Media Manager and order entry; Navigator books directly in the EHR and honors EHR communication preferences.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Virtua Health: 3,500+ Cologuard tests completed in five months via automated preventive outreach (vendor-reported)",
          "Navigator outbound voice/SMS expansion for care gaps (MobiHealthNews, Sept 2026)"
        ],
        "sources": [
          {
            "title": "Epic + Luma reduces gaps in care and revenue risk",
            "url": "https://go.lumahealth.io/luma-integrated-with-epic",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Luma Health expands AI outreach to fill schedules, close care gaps (MobiHealthNews)",
            "url": "https://www.mobihealthnews.com/news/luma-health-expands-ai-outreach-fill-schedules-close-care-gaps",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Artera (formerly WELL Health)",
        "parentCompany": "Artera (ownership not stated)",
        "category": "Patient outreach for gap closure",
        "capabilitySummary": "Patient communications platform with AI agents and campaign tools, including care gap closure outreach, no-show follow-up and scheduling conversations.",
        "deploymentModel": "SaaS with EHR integrations.",
        "integrationNotes": "EHR integrations listed; MEDITECH Expanse Patient Connect is powered by Artera. Epic-specific evidence not found on page opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Hackensack Meridian Health mammography campaign: 45% response, $2.7M incremental revenue; Beauregard Health: +18% mammography, +13% colorectal screening in two months (vendor case studies)",
          "Site banner states 2026 Best in KLAS for Patient Communications (vendor claim, not verified on KLAS)"
        ],
        "sources": [
          {
            "title": "Artera health systems resources",
            "url": "https://artera.io/resource-org-size/health-systems/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Get Well",
        "parentCompany": "SAIGroup",
        "category": "Patient outreach for gap closure",
        "capabilitySummary": "Digital patient engagement and navigation platform (Get Well 360) spanning inpatient point-of-care engagement, post-discharge navigation and AI texting; payer programs include care gap closure and SDoH screening.",
        "deploymentModel": "SaaS plus in-room and mobile engagement.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Q4 2024 renewals/expansions with 58 health systems and VISNs, incl. Intermountain Health, UMass Memorial, Adventist Health",
          "Payer care-gap use case; Sutter Health | Aetna 25% retention improvement (2022)"
        ],
        "sources": [
          {
            "title": "Get Well Q4 2024 expansions, renewals and launches",
            "url": "https://www.getwellnetwork.com/pr/get-well-q4-2024-expansions-renewals-launches/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Get Well Expands Digital Engagement to Payers to Close Care Gaps (Business Wire, 2022)",
            "url": "https://www.businesswire.com/news/home/20220308005379/en/Get-Well-Expands-Digital-Engagement-Solution-to-Payers-to-Close-Care-Gaps-and-Improve-Retention",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Lirio",
        "parentCompany": "Lirio (ownership not stated)",
        "category": "Patient outreach for gap closure",
        "capabilitySummary": "Behavioral science and agentic AI platform (Precision Nudging) that sends adaptive, individualized omnichannel messages to change patient behavior; launched a life sciences version in Oct 2025 extending its care delivery offering.",
        "deploymentModel": "SaaS messaging platform; HITRUST CSF and SOC 2 Type II.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "No health system customers named in release opened"
        ],
        "sources": [
          {
            "title": "Lirio launches Precision Nudging for Life Sciences (Business Wire, Oct 2025)",
            "url": "https://www.businesswire.com/news/home/20251028738199/en",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Upfront Healthcare Services",
        "parentCompany": "Health Catalyst (agreement Jan 2025; listed among 2025 acquisitions)",
        "category": "Patient outreach for gap closure",
        "capabilitySummary": "Patient engagement and access platform with analytics that uses clinical, sociodemographic and patient-reported data to reengage patients, guide them to needed care, support scheduling, referrals and care transitions.",
        "deploymentModel": "SaaS.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Described as a KLAS Research category leader serving health systems and provider groups"
        ],
        "sources": [
          {
            "title": "Health Catalyst to acquire Upfront Healthcare Services (SEC exhibit 99.1)",
            "url": "https://www.sec.gov/Archives/edgar/data/1636422/000163642225000010/exhibit991-upfrontpressrel.htm",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Health Catalyst year in review (HCI)",
            "url": "https://www.hcinnovationgroup.com/finance-revenue-cycle/article/55336749/new-leadership-partnerships-and-platforms-mark-health-catalysts-transformative-year",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Cured",
        "parentCompany": "Innovaccer (acquired Jan 2024)",
        "category": "Patient outreach for gap closure",
        "capabilitySummary": "Healthcare CRM and digital marketing/outreach platform now part of Innovaccer; brought 20+ health system customers at acquisition.",
        "deploymentModel": "SaaS CRM.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Acquisition Jan 2024; Best in KLAS distinction cited for Cured in 2026"
        ],
        "sources": [
          {
            "title": "Innovaccer (Wikipedia)",
            "url": "https://en.wikipedia.org/wiki/Innovaccer",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Salesforce Health Cloud",
        "parentCompany": "Salesforce, Inc.",
        "category": "Patient outreach for gap closure",
        "capabilitySummary": "CRM platform for care coordination with a Care Gaps object that tracks gaps sourced from external systems and links them to support processes for closure; Agentforce AI agents for health announced 2025.",
        "deploymentModel": "Multi-tenant cloud CRM.",
        "integrationNotes": "Care Gap records are created by external source systems; no Epic-specific evidence in source opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Care Gaps feature documentation"
        ],
        "sources": [
          {
            "title": "Enable Care Teams to Track Gaps (Salesforce Health Cloud docs)",
            "url": "https://developer.salesforce.com/docs/atlas.en-us.health_cloud.meta/null/enable_care_gaps.htm",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Aledade",
        "parentCompany": "Aledade (independent)",
        "category": "Value-based care enablement partners (risk-sharing operating partners)",
        "capabilitySummary": "Physician-led value-based care company that runs ACO portfolios (incl. MSSP) for practices and health systems, with the Aledade App and Aledade Assist surfacing prioritized gap and outreach worklists in the EHR and data aggregated from plans, labs, pharmacies and hospitals.",
        "deploymentModel": "Partnership with shared-savings economics plus software and data feeds.",
        "integrationNotes": "Aledade Assist embeds data in existing EHRs during encounters; structured ADT feeds; no Epic-specific evidence on page opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": "Shared-savings model (Aledade succeeds when partners lower cost and improve quality); no fee amounts published.",
        "evidence": [
          "Vendor: 93% of Aledade MSSP ACOs achieved shared savings in 2024 vs. 73% national"
        ],
        "sources": [
          {
            "title": "Aledade for hospitals and health systems",
            "url": "https://aledade.com/hospitals-and-health-systems",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Lumeris (Tom)",
        "parentCompany": "Lumeris (ownership not stated in source opened)",
        "category": "Value-based care enablement partners (risk-sharing operating partners)",
        "capabilitySummary": "Value-based care operating partner; Tom is an AI primary care platform of agents for performance tracking, best-next-action workflows and automated patient outreach with clinicians in the loop.",
        "deploymentModel": "Partnership plus AI platform.",
        "integrationNotes": "Lumeris states deep integrations with major EHRs such as Epic (Jan 2026).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Hackensack Meridian Health expanding Lumeris partnership to its MSSP ACO (Jan 2026)"
        ],
        "sources": [
          {
            "title": "Hackensack Meridian to Deploy Lumeris' AI Tool in MSSP ACO (HCI)",
            "url": "https://www.hcinnovationgroup.com/policy-value-based-care/accountable-care-organizations-acos/news/55353377/hackensack-meridian-to-deploy-lumeris-ai-tool-in-mssp-aco",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Clarify Health",
        "parentCompany": "Clarify Health Solutions (venture-backed)",
        "category": "Healthcare data, benchmarking and market/performance intelligence",
        "capabilitySummary": "Atlas platform mapping ~300M patient lives for provider performance benchmarking, network and value-based payment analytics and price transparency analytics.",
        "deploymentModel": "SaaS analytics.",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Included in Gartner Hype Cycle for U.S. Healthcare Payers 2024 (vendor release)"
        ],
        "sources": [
          {
            "title": "Clarify Health Gartner Hype Cycle release (Business Wire, Aug 2024)",
            "url": "https://www.businesswire.com/news/home/20240815260327/en",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Kythera Labs (Wayfinder)",
        "parentCompany": "Kythera Labs (ownership not verified)",
        "category": "Healthcare data, benchmarking and market/performance intelligence",
        "capabilitySummary": "Wayfinder data platform that harmonizes and patient-masters multi-source healthcare data with modules for attribution, patient journeys, forecasting and cohort design, for strategy and data science teams.",
        "deploymentModel": "Kythera-hosted or inside the customer's Databricks environment (Databricks ISV partner).",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Serves health systems, life sciences, payers (per site navigation)"
        ],
        "sources": [
          {
            "title": "Kythera Wayfinder",
            "url": "https://kytheralabs.com/products/wayfinder",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Milliman MedInsight",
        "parentCompany": "Milliman",
        "category": "Healthcare data, benchmarking and market/performance intelligence",
        "capabilitySummary": "Claims-based analytics for payers, providers and ACOs: provider quality and outcomes dashboards, risk adjustment, at-risk group identification and value-based contract modeling.",
        "deploymentModel": "SaaS (listed on Microsoft marketplace).",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Used by 300+ payers, providers/ACOs, employers and agencies (Mar 2026 release)",
          "Best in KLAS 2025 PHM: MedInsight Value-Based Care Platform listed (not ranked), score 74.5"
        ],
        "sources": [
          {
            "title": "Milliman MedInsight integrates AI and next-generation analytics (Mar 2026)",
            "url": "https://digital-release.wgnradio.com/business/press-releases/cision/20260303SF99818/milliman-medinsight-integrates-ai-and-next-generation-analytics-to-strengthen-value-based-care-decision-making",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Best in KLAS 2025: Population Health Management",
            "url": "https://klasresearch.com/best-in-klas-ranking/population-health-management/2025/256",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "AnewHealth (ExactCare + Tabula Rasa HealthCare)",
        "parentCompany": null,
        "category": "Medication adherence, pharmacy analytics and medication optimization",
        "capabilitySummary": "Clinical pharmacy care and national dispensing, MedWise medication-regimen technology, pharmacy benefit management and PACE support for complex chronic populations.",
        "deploymentModel": "managed service",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Serves risk-bearing providers including PACE, primary and specialty care providers, kidney care, home health and managed care plans (vendor)."
        ],
        "sources": [
          {
            "title": "AnewHealth: About Us",
            "url": "https://anewhealthrx.com/about-us/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "ExactCare and Tabula Rasa HealthCare rebrand combined organization as AnewHealth",
            "url": "https://www.exactcarepharmacy.com/about-exactcare/news/exactcare-and-tabula-rasa-healthcare-rebrand-combined-organization-as-anewhealth/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "ActiumHealth",
        "parentCompany": "Syllable Corporation (purchase announced March 20, 2024)",
        "category": "Patient outreach for gap closure",
        "capabilitySummary": "AI agents that identify and rank patients likely to need services and run outbound outreach, appointment scheduling directly in the EHR and referral follow-up.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Referral page describes detecting signed referral orders in Epic/Cerner and writing status back to the order; API mechanics are not described.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Virtua Health case study: 25K appointments and 21x ROI from targeted outreach.",
          "Logos include MemorialCare, Nebraska Medicine, MultiCare, Legacy Health, Nuvance Health and Catholic Health."
        ],
        "sources": [
          {
            "title": "ActiumHealth",
            "url": "https://www.actiumhealth.com/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Syllable Corporation announces purchase of Actium Health (Healthcare IT Today)",
            "url": "https://www.healthcareittoday.com/2024/04/23/syllable-corporation-announces-purchase-of-actium-health/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "ChatGPT"
        ]
      }
    ],
    "deliveryPath": {
      "lean": "combined",
      "note": "An Epic IDN already licenses Healthy Planet, which KLAS ranked second in 2025 population health and which covers registries, risk stratification, gap prompts, campaigns and QRDA submission, so most of the base capability is configure-not-buy. The gaps that usually justify purchase are data the health system does not hold (payer claims, dispensed-medication history for PDC adherence measures), payer gap reconciliation across many contracts, and outreach or pharmacist capacity at scale; these are often bought as data feeds or services rather than a second platform. Building in-house prioritization models on Epic and Cosmos data is feasible, but measure certification (NCQA), model validation and ongoing payer reconciliation carry recurring cost that vendors amortize across customers."
    },
    "rfpQuestions": [
      "Which HEDIS, Stars Part D (PDC-DR, PDC-RASA, PDC-STA, SUPD) and MSSP/APP quality measures does your engine calculate, and are they NCQA-certified for the current measurement year?",
      "What is your source for dispensed-medication and pharmacy claims data for patients across all payers, how current is it, and how do you reconcile it with Epic medication lists?",
      "How do care gaps, risk scores and recommended medication interventions (e.g., statin initiation, adherence outreach) appear inside Epic workflows: Best Practice Advisory, Healthy Planet registries, In Basket, or a separate application? Name live Epic sites.",
      "How do you reconcile your gap lists with each payer's gap reports, and what is the process when they disagree?",
      "For risk stratification models: what outcomes are predicted for diabetes, heart failure and COPD panels, how were they validated on populations like ours, and how are performance drift and subgroup bias monitored and reported?",
      "Who performs the intervention (our care managers, your pharmacists, or automated outreach), and how is outreach prioritized by expected gap closure or avoided utilization rather than raw risk score?",
      "How do you attribute gap closures, quality score changes and avoided admissions to your program, and will you share patient-level results for our own evaluation?",
      "What is the implementation timeline, required internal FTEs (Epic analysts, data engineers), and pricing basis (PMPM, per attributed life, per enrolled patient, or shared savings)?"
    ],
    "sources": [],
    "excluded": [
      {
        "name": "Jvion (standalone)",
        "reason": "AI and SDoH assets acquired by Lightbeam Health Solutions in June 2022; covered under Lightbeam."
      },
      {
        "name": "Tabula Rasa HealthCare / SinfoníaRx / Cureatr (standalone)",
        "reason": "Tabula Rasa sold SinfoníaRx to Cureatr in March 2023 and was taken private; AssureCare acquired Cureatr and SinfoníaRx technology in June 2024. Covered under AssureCare."
      },
      {
        "name": "Pieces Technologies",
        "reason": "Acquired by Smarter Technologies (Sept 2025); products are clinical documentation and note automation, not risk stratification or gap closure."
      },
      {
        "name": "Abacus Insights",
        "reason": "Search results describe a health-plan data management and interoperability platform; no opened source tied it to provider-side gap closure, so it was not profiled."
      },
      {
        "name": "Premier Inc.",
        "reason": "Taken private by Patient Square Capital (closed Nov 2025); sources opened did not describe an ambulatory care-gap or adherence product. Hospital analytics/benchmarking overlap makes it a potential competitor/acquirer to watch."
      },
      {
        "name": "Vizient (Clinical Data Base)",
        "reason": "Hospital risk-adjusted benchmarking (mortality, LOS, readmissions) used by 1,600+ facilities; no population health or care-gap capability on product page. Potential competitor in analytics, not a fit for this use case."
      },
      {
        "name": "Evolent",
        "reason": "Payer-focused specialty and value-based services; ownership status after 2024 sale talks not verified in sources opened."
      },
      {
        "name": "MEDITECH Expanse Population Health, athenahealth, NextGen Healthcare population health",
        "reason": "EHR-native modules for non-Epic EHRs; not applicable to an Epic IDN except for affiliated practices (NextGen ranked 6th in Best in KLAS 2025 PHM)."
      },
      {
        "name": "Relevant Healthcare, Salient Healthcare, COPE Health Solutions ARC, The Garage",
        "reason": "Appear in Best in KLAS 2025 PHM unranked list; primarily serve community health centers or smaller organizations and were not researched further for a Tier 1 IDN."
      },
      {
        "name": "MEDITECH Expanse (Care Compass and Registries)",
        "reason": "Proposed by ChatGPT research; not listed: Real and current, but an EHR-native module for a non-Epic EHR; not applicable to an Epic IDN. Already excluded in our opp-2 list, and MEDITECH is covered in opp-1 for affiliate scenarios."
      },
      {
        "name": "NextGen Population Health",
        "reason": "Proposed by ChatGPT research; not listed: Real; ranked 6th in KLAS 2025 PHM, but an ambulatory-EHR-oriented module. Already excluded in our opp-2 list; NextGen is covered in opp-3 as EHR-native referral functionality."
      },
      {
        "name": "The Garage (Bridge)",
        "reason": "Proposed by ChatGPT research; not listed: Real and listed (unranked) in KLAS 2025 PHM, but references are ACOs and IPAs (e.g., Network Solutions IPA, Belay Health Solutions); no Tier 1 IDN customer or Epic artifact found. Consistent with our existing exclusion."
      },
      {
        "name": "Relevant Healthcare",
        "reason": "Proposed by ChatGPT research; not listed: Real, but customers are community health centers (90+ health centers). Not a fit for a Tier 1 IDN; consistent with our existing exclusion."
      },
      {
        "name": "COPE Health Solutions (ARC)",
        "reason": "Proposed by ChatGPT research; not listed: Real, but the provider page targets IPAs, CINs, ACOs, FQHCs and physician groups; only named reference is Sun River Health (FQHC). No health-system customer named; consistent with our existing exclusion."
      },
      {
        "name": "Salient Health",
        "reason": "Proposed by ChatGPT research; not listed: Real and explicitly targets health systems, with relevant care-gap and medication-adherence analytics, but named users are a state agency, Bluestone and Rocky Mountain ACO; no Tier 1 IDN reference found. Worth a watch-list note given its KLAS score."
      }
    ]
  },
  "opp-3": {
    "asOf": "2026-10-21",
    "generation": "pre-generated, reviewed by hand",
    "categories": [
      {
        "name": "Referral management and closed-loop referral platforms",
        "covers": "Referral intake (including fax), routing, specialist matching, eConsults, status tracking and loop closure between referring and receiving providers.",
        "deploymentModels": [
          "Cloud SaaS",
          "EHR-embedded app / API integration",
          "Tech-enabled service (concierge scheduling)"
        ],
        "fitConsiderations": "For an Epic IDN, the main question is whether the tool writes referrals, orders and documents back into Epic work queues (FHIR/HL7, Media Manager) or runs a parallel queue. Newer AI intake vendors focus on fax-to-order automation; older platforms focus on network-wide tracking and referring-provider portals."
      },
      {
        "name": "Referral leakage / network integrity analytics",
        "covers": "Claims- and EHR-based analysis of where referred patients receive care, in-network retention, referral corridors, physician split-share and leakage by service line and geography.",
        "deploymentModels": [
          "Cloud SaaS analytics",
          "Data licensing",
          "Analytics platform modules"
        ],
        "fitConsiderations": "All-payer claims datasets show out-of-network destinations that the IDN's own Epic data cannot; EHR-derived views show order-level detail and loop status that claims cannot. Most vendors in this group also sell broader strategy, market or population-health analytics."
      },
      {
        "name": "Patient access, navigation and scheduling",
        "covers": "Consumer and access-center provider search, provider matching rules, online and guided scheduling, AI agents for scheduling and referral conversion, capacity routing.",
        "deploymentModels": [
          "Cloud SaaS",
          "Epic-integrated apps (App Orchard / Showroom / Vendor Services)",
          "Marketplace (Zocdoc)"
        ],
        "fitConsiderations": "Value depends on write-back to Epic Cadence schedules and on the quality of the provider data feeding matching rules. Overlap with MyChart and Epic access-center tooling should be mapped before purchase."
      },
      {
        "name": "EHR-native referral functionality",
        "covers": "Referral orders, referral work queues, cross-organization referral exchange and affiliate access built into the EHR.",
        "deploymentModels": [
          "EHR module (included or licensed)",
          "Hosted affiliate extension (e.g., Epic Community Connect)"
        ],
        "fitConsiderations": "Usually the system of record; third-party tools are judged on how they extend it. Cross-EHR loop closure relies on standards such as 360X and Direct, which require the receiving organization to participate."
      },
      {
        "name": "Provider data, directory and interoperability infrastructure",
        "covers": "Provider master data (locations, specialties, network/plan participation, availability), directory APIs, provider registries and integration engines that move referral data.",
        "deploymentModels": [
          "Cloud SaaS / API",
          "On-premises or hosted integration engine"
        ],
        "fitConsiderations": "Accurate provider and plan data drives in-network matching; poor data undermines every other category. Several access vendors (Kyruus Health, Loyal/Clarify) bundle provider data management."
      },
      {
        "name": "Physician relationship management / outreach CRM",
        "covers": "Physician liaison territory management, outreach activity tracking, referral-source targeting and campaign tools for referral development.",
        "deploymentModels": [
          "Cloud CRM platform plus partner configuration",
          "Healthcare-specific CRM SaaS"
        ],
        "fitConsiderations": "General CRM platforms need claims/referral data and EHR feeds to be useful for leakage work; healthcare-specific CRMs bundle data but may be narrower. Several leakage-analytics vendors position their data as a feed into PRM."
      },
      {
        "name": "Adjacent closed-loop referral networks (post-acute and social care)",
        "covers": "Closed-loop referral networks for post-acute discharge and social-care needs, often integrated with Epic case management or Healthy Planet.",
        "deploymentModels": [
          "Cloud network SaaS",
          "Epic-integrated app"
        ],
        "fitConsiderations": "Not specialist referral tools, but a domain reviewer may expect them in a referral-network landscape; they are often already licensed at Tier 1 IDNs and their network-tracking patterns are relevant to specialist loop closure."
      }
    ],
    "vendors": [
      {
        "name": "ReferralMD",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Referral management and closed-loop referral platforms",
        "capabilitySummary": "Cloud platform covering referral management, eConsults, appointment management, fax management, patient intake and communications, and marketing CRM and analytics for hospitals, health systems, networks and payers.",
        "deploymentModel": "Cloud SaaS; web and mobile-optimized access.",
        "integrationNotes": "AVIA Marketplace listing names Epic among supported EMRs and lists API, FHIR, HL7 and Direct connectivity; EHR integration described as recommended but not required.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "AVIA Marketplace: ReferralMD Platform",
            "url": "https://marketplace.aviahealth.com/product/25090/referralmd-platform",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Tennr",
        "parentCompany": "Independent (venture-backed)",
        "category": "Referral management and closed-loop referral platforms",
        "capabilitySummary": "Orchestration platform and specialized language models (RaeLM) that automate intake of referral documents, check them against payer criteria, and provide referral status visibility to referring and receiving providers through Tennr Network.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "Source states it works without EMR replacement; no specific Epic integration evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Pulse 2.0: Tennr raises $101 million for automating referral-based care",
            "url": "https://pulse2.com/tennr-101-million-raised-for-automating-referral-based-care",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Janus Health",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Referral management and closed-loop referral platforms",
        "capabilitySummary": "AI referral intake that extracts and validates data from faxed and handwritten referrals, detects duplicates, creates referral orders and tracks referrals from intake through scheduling.",
        "deploymentModel": "Cloud SaaS integrated with the EHR.",
        "integrationNotes": "Vendor states it creates referral orders in Epic automatically, uses FHIR APIs for patient matching, HL7 to push referrals into EHR work queues, and imports faxes via Epic Media Manager.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Janus Health: Referral management process checklist",
            "url": "https://www.janus-ai.com/blog/referral-management-process-checklist/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Healthcare IT News: How health systems can stop losing money on referrals",
            "url": "https://www.healthcareitnews.com/news/how-health-systems-can-stop-losing-money-referrals-while-boosting-patient-care",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "LeadingReach",
        "parentCompany": "Independent; growth investment from Enhanced Healthcare Partners (July 2026)",
        "category": "Referral management and closed-loop referral platforms",
        "capabilitySummary": "Referral network that consolidates fax, email, Direct and web-form referrals into one queue, gives referring providers free accounts to see referral status, and reports referral-to-appointment and aging metrics.",
        "deploymentModel": "Cloud SaaS network with tiered plans from free to enterprise.",
        "integrationNotes": "States it works across EHRs; athenahealth Marketplace partner (Sept 2026). No Epic-specific evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "LeadingReach: Enhanced Healthcare Partners growth investment",
            "url": "https://leadingreach.com/news/enhanced-healthcare-partners-announced-its-growth-investment-in-leadingreach/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "LeadingReach product page",
            "url": "https://leadingreach.com/outreach-crm",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "AristaMD",
        "parentCompany": "Independent (acquired Preferral in 2022)",
        "category": "Referral management and closed-loop referral platforms",
        "capabilitySummary": "Combines eConsults, patient-to-specialist matching by insurance, location and sub-specialty, and electronic referral sending and tracking for primary care; acquired referral platform Preferral in February 2022.",
        "deploymentModel": "Cloud SaaS plus optional referral coordination service.",
        "integrationNotes": "Described as interoperable; no Epic-specific evidence found in sources opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Fierce Healthcare: AristaMD launches platform for PCP specialty referrals and eConsults",
            "url": "https://www.fiercehealthcare.com/providers/aristamd-launches-solution-expand-access-specialty-care-pcps",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Healthcare IT Today: AristaMD acquires Preferral",
            "url": "https://www.healthcareittoday.com/2022/02/11/aristamd-acquires-referral-management-solution-preferral-to-increase-patient-access-to-specialty-care/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "ReferWell",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Referral management and closed-loop referral platforms",
        "capabilitySummary": "Care scheduling platform ('Perfect Match') that matches patients with local providers and books appointments, paired with a concierge scheduling service and referral management support for health plans and provider organizations.",
        "deploymentModel": "Cloud platform plus tech-enabled concierge service.",
        "integrationNotes": "No EHR integration details in sources opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Business Wire: Essen Health Care selects ReferWell (2024-03-12)",
            "url": "https://www.businesswire.com/news/home/20240312997249/en/Essen-Health-Care-Selects-ReferWell-as-Their-Strategic-Partner-to-Improve-Access-to-Care",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Phreesia (Referral Management)",
        "parentCompany": "Phreesia",
        "category": "Referral management and closed-loop referral platforms",
        "capabilitySummary": "Specialist-side referral work queues that standardize faxed and electronic referrals, prioritize by value and urgency, run eligibility checks and send automated outreach to referred patients.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "Referral page does not mention Epic integration.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Phreesia: Referral Management",
            "url": "https://phreesia.com/?p=42418",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Notable",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "AI agent workflows for patient access that automate faxed referral intake, convert referrals into scheduled appointments, support self-scheduling and handle MyChart inquiries.",
        "deploymentModel": "Cloud SaaS with low-code configurable workflows.",
        "integrationNotes": "Vendor's Epic page states it writes data back into the EHR and complements MyChart rather than replacing it.",
        "typicalTimeToDeploy": "6-12 weeks for pre-built workflows (vendor-stated)",
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Notable: Patient access automation for Epic",
            "url": "https://www.notablehealth.com/lp/access-landing/access-landing-epic",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Clarify Health (including Loyal)",
        "parentCompany": "Clarify Health (acquired Loyal Health Holdings, June 2026)",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "Clarify's Meridian platform and referral analytics quantify referral leakage and high-value referral corridors from payer-complete patient journeys; the Loyal acquisition adds provider data management, care search, real-time scheduling and CRM to connect leakage analysis to patient activation.",
        "deploymentModel": "Cloud SaaS analytics plus patient activation platform.",
        "integrationNotes": "Loyal is described as having 'native EHR integrations'; Epic not named in sources opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Healthcare IT Today: Clarify Health acquires Loyal Health (2026-06-19)",
            "url": "https://www.healthcareittoday.com/2026/06/19/clarify-health-acquires-loyal-health-to-create-healthcares-first-closed-loop-network-intelligence-and-patient-activation-platform/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "AHA: Clarify case study - health system enhances care continuity",
            "url": "https://www.aha.org/system/files/media/file/2024/11/Clarify-atlas-case-study-health-system-enhances-care-continuity.pdf",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Definitive Healthcare (Network Intelligence)",
        "parentCompany": "Definitive Healthcare",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "Claims- and affiliation-based analytics on referral patterns, shared patient pathways and patient in- and out-migration, positioned to support provider relationship management.",
        "deploymentModel": "Cloud SaaS data and analytics subscription.",
        "integrationNotes": "No Epic-specific evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "SoftwareOne marketplace: Network Intelligence by Definitive Healthcare",
            "url": "https://platform.softwareone.com/product/network-intelligence/PCP-5155-7538",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Definitive Healthcare case study: influencing patient referrals and leakage",
            "url": "https://www.definitivehc.com/resources/case-study/influence-patient-referrals-leakage-market-expansion",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Trilliant Health",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "All-payer claims-based analytics (300M+ individuals) including a National Provider Directory with referral destinations and network integrity views, and Oria, an AI strategist with a referral leakage and network alignment use case.",
        "deploymentModel": "Cloud SaaS; Oria Lite free tier and paid premium tier.",
        "integrationNotes": "No Epic-specific evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": "Oria Lite is free; premium Oria pricing available on request (vendor page).",
        "evidence": [],
        "sources": [
          {
            "title": "Trilliant Health: Network integrity / National Provider Directory",
            "url": "https://www.trillianthealth.com/analytics/network-integrity",
            "retrieved": "2026-10-21"
          },
          {
            "title": "HIT Consultant: Trilliant Health launches Oria AI strategist",
            "url": "https://hitconsultant.net/2026/09/24/trilliant-health-launches-oria-ai-strategist-healthcare-claims-analytics/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Trella Health",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "Claims-based referral and market analytics, including a Medicare Advantage dataset used to track referral destinations and out-of-network use by specialty, with quarterly updates.",
        "deploymentModel": "Cloud SaaS analytics.",
        "integrationNotes": "No EHR or CRM integration evidence found in source opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Trella Health blog: Medicare Advantage referral analytics (2025-07-15)",
            "url": "https://www.trellahealth.com/?p=22522",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Strata Decision Technology (Axiom Referral Patterns, ex-Syntellis)",
        "parentCompany": "Roper Technologies",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "Axiom Referral Patterns, launched by Syntellis in 2023 and now part of Strata, maps physician referral patterns and leakage using an all-payer claims dataset, alongside Strata's financial planning and decision-support software.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "No Epic-specific evidence found for the referral module.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Business Wire: Syntellis launches Axiom Referral Patterns (2023)",
            "url": "https://www.businesswire.com/news/home/20230628536825/en/Syntellis-Performance-Solutions-Launches-Axiom-Referral-Patterns-A-New-Data-Insights-Solution-to-Drive-Strategic-Growth-for-Healthcare-Organizations",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Built In Chicago: Strata combines with Syntellis",
            "url": "https://www.builtinchicago.org/articles/strata-combines-with-syntellis",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Health Catalyst (Network Management)",
        "parentCompany": "Health Catalyst",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "Network management analytics that combine claims, clinical and CRM data to track patient pathways, out-of-network drivers and referral behavior by provider and specialty; a separate Referral Management product is offered.",
        "deploymentModel": "Cloud analytics platform.",
        "integrationNotes": "Page does not describe EHR connectivity.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Health Catalyst: Network management",
            "url": "https://healthcatalyst.com/healthcare-analytics/population-health-value-based-care/network-management",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Arcadia (Network Modeler)",
        "parentCompany": "Arcadia",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "Network Modeler visualizes referral flows and leakage and supports referral pathways to in-network specialists, using national provider performance datasets and Medicare market data; aimed at payers and risk-bearing provider organizations.",
        "deploymentModel": "Cloud SaaS analytics.",
        "integrationNotes": "No Epic-specific evidence in tear sheet.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Arcadia: Network Modeler tear sheet",
            "url": "https://arcadia.io/assets/Network-Modeler-Tear-Sheet.pdf",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Innovaccer (Referral Management / Comet)",
        "parentCompany": "Innovaccer",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "Referral management with point-of-care referral creation, specialist filters for cost, quality and network status, OCR of faxed orders, a network leakage dashboard, and AI agents (Referral Copilot, Scheduling AI Agent) in its Comet access-center product.",
        "deploymentModel": "Cloud SaaS on Innovaccer's Healthcare Intelligence Cloud.",
        "integrationNotes": "AVIA listing names Epic among supported EMRs with a bi-directional HL7/FHIR interface.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "AVIA Marketplace: Innovaccer Referral Management",
            "url": "https://marketplace.aviahealth.com/product/64709",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Business Wire: Olympia Orthopaedic Associates taps Innovaccer (2025-10-08)",
            "url": "https://www.businesswire.com/news/home/20251008423565/en/Olympia-Orthopaedic-Associates-Taps-Innovaccer-to-Automate-Referrals-and-Scheduling-with-AI-Agents",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Innovaccer blog: reducing network leakage with referral management (2021)",
            "url": "https://innovaccer.ai/blogs/reducing-network-leakage-and-improve-physician-and-patient-engagement-through-a-comprehensive-referral-management-solution",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Lightbeam Health (Referral Management)",
        "parentCompany": "Lightbeam Health",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "Referral module within a population health platform that ranks preferred-network specialists by performance, insurance, distance and language, sends patient reminders and tracks each referral step.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "No EHR integration detail on referral page or case study.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Lightbeam: Referral management",
            "url": "https://lightbeamhealth.com/solutions/referral-management/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Lightbeam case study: referral management (Vanguard Medical Group)",
            "url": "https://lightbeamhealth.com/wp-content/uploads/2026/01/Case-Study-Referral-Management.pdf",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Kyruus Health",
        "parentCompany": "RevSpring (acquisition announced Sept 2025; ChatGPT research reports completion in 2026)",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Provider data management, provider search and directory, patient self-scheduling, digital check-in and listings management for health systems and health plans; absorbed HealthSparq (2020).",
        "deploymentModel": "Cloud SaaS with APIs.",
        "integrationNotes": "Lists Epic, Oracle Health, MEDITECH and athenahealth integrations; joined Epic App Orchard in 2018 to book into Epic schedules from ProviderMatch (Piedmont Healthcare an early joint customer).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Kyruus Health home page",
            "url": "https://kyruushealth.com/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Fierce Healthcare: RevSpring set to buy Kyruus Health",
            "url": "https://www.fiercehealthcare.com/health-tech/revspring-set-buy-kyruus-health-combine-provider-search-scheduling-and-payment",
            "retrieved": "2026-10-21"
          },
          {
            "title": "TechTarget: RevSpring acquires Kyruus",
            "url": "https://www.techtarget.com/patientengagement/news/366631620/RevSpring-acquires-Kyruus-to-fuse-provider-search-functions",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Kyruus: joins Epic App Orchard with direct scheduling",
            "url": "https://kyruushealth.com/kyruus-joins-the-epic-app-orchard-with-direct-scheduling-solution/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Healthcare IT News: Kyruus acquires HealthSparq",
            "url": "https://www.healthcareitnews.com/news/kyruus-acquires-healthsparq-cambia-health-solutions",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Luma Health",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Patient access platform whose fax automation identifies inbound and outbound referrals, creates referrals in the EHR, routes them to teams and supports scheduling and loop closure.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "Vendor lists an Epic integration (e.g., updating Epic Health Maintenance, creating EHR referrals); vendor PDF 'Luma Integrated with Epic' exists but was not opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Luma Health: Referral management",
            "url": "https://www.lumahealth.io/product/referral-management/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "DexCare",
        "parentCompany": "Independent (spun out of Providence)",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Access platform that matches patient demand to available capacity across ambulatory services, routes patients to the appropriate service and supports virtual care and digital front door scheduling.",
        "deploymentModel": "Cloud platform-as-a-service.",
        "integrationNotes": "Sources describe use of existing EMR investments; Epic not named in sources opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Mass General Brigham: implements DexCare",
            "url": "https://news.massgeneralbrigham.org/en/mass-general-brigham-implements-dexcare-to-support-care-integration-and-advance-digital-health",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Chief Healthcare Executive: DexCare CEO on capacity constraints",
            "url": "https://chiefhealthcareexecutive.com/view/dexcare-ceo-pushes-against-capacity-constraints-in-hospitals",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Optum Patient Access and Engagement (formerly DocASAP)",
        "parentCompany": "Optum (UnitedHealth Group) - per Dealroom profile; acquisition details not verified",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Patient-provider matching by insurance and clinical need, online scheduling, reminders and provider data management for health systems, health plans and physician groups.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "No Epic evidence in sources opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Dealroom: DocASAP (now Optum Patient Access and Engagement)",
            "url": "https://app.dealroom.co/companies/docasap",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Zocdoc",
        "parentCompany": "Independent",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Consumer scheduling marketplace with real-time availability syncs into provider scheduling systems, and an enterprise Care Access Network (Sept 2026) that lets payers and platforms embed Zocdoc booking.",
        "deploymentModel": "Marketplace plus API / data feed / MCP server.",
        "integrationNotes": "Zocdoc states it has an integration on Epic App Orchard (2022 release).",
        "typicalTimeToDeploy": "Enterprise partner projects 'stood up within hours and fully implemented in a few weeks' (vendor-stated, for payer/platform partners)",
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Fierce Healthcare: Zocdoc enterprise offering",
            "url": "https://www.fiercehealthcare.com/health-tech/zocdoc-power-bookings-payer-tech-platforms-enterprise-offering",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Zocdoc: 150th Zocdoc Sync",
            "url": "https://www.zocdoc.com/about/news/zocdoc-announces-completion-of-its-150th-zocdoc-sync-powering-online-booking-and-driving-scheduling-interoperability-across-the-healthcare-industry/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Experian Health (Patient Schedule)",
        "parentCompany": "Experian",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Online self-scheduling and guided call-center scheduling driven by provider business rules, with reminders and outreach campaigns; vendor states it can keep referrals within a targeted set of specialists.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "Product page does not mention Epic.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Experian Health: Patient Schedule",
            "url": "https://www.experian.com/healthcare/products/payment-tools/online-patient-appointment-scheduling",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Hyro",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Conversational AI assistants for web, phone and SMS that let patients book, reschedule and cancel appointments and handle prescription refills.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "Vendor states integration with Epic scheduling, Epic Willow Ambulatory and deployment via Epic Vendor Services.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Hyro: Epic integration / Weill Cornell Medicine",
            "url": "https://www.hyro.ai/?p=2687",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Fabric",
        "parentCompany": "Independent (acquired GYANT and Zipnosis)",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Care enablement platform with conversational AI symptom intake, navigation to the right care setting and self-scheduling through an organization-specific provider directory.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "No Epic evidence in source opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Pulse 2.0: Why Fabric is buying GYANT",
            "url": "https://pulse2.com/why-fabric-is-buying-gyant/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Epic (Referrals, Care Everywhere, EpicCare Link, Community Connect, Cadence)",
        "parentCompany": "Epic Systems Corporation",
        "category": "EHR-native referral functionality",
        "capabilitySummary": "Epic referral orders and work queues, Care Everywhere record exchange and Care Everywhere Referrals Management (CERM) for Epic-to-Epic referral tracking, 360X closed-loop referral exchange with non-Epic systems, EpicCare Link for community referrers, and Community Connect for extending the host system's Epic instance to affiliates.",
        "deploymentModel": "EHR modules; Community Connect hosted by the IDN for affiliates.",
        "integrationNotes": "Native. open.epic documents 360X closed-loop referrals (current integrations listed: eClinicalWorks, Netsmart) and Direct-message flags for specialist reports.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "open.epic: Exchanging clinical findings (EHR-to-EHR, 360X)",
            "url": "https://open.epic.com/clinical/ehrtoehr",
            "retrieved": "2026-10-21"
          },
          {
            "title": "UC Davis Health: How to use CERM",
            "url": "https://health.ucdavis.edu/referrals/ways-to-refer/how-to-use-CERM",
            "retrieved": "2026-10-21"
          },
          {
            "title": "CSI Companies: Epic Community Connect and referral leakage",
            "url": "https://www.csicompanies.com/insights/epic-community-connectnbsp-how-health-systems-use-it-to-eliminate-referral-leakage",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT",
          "Gemini"
        ]
      },
      {
        "name": "Oracle Health (Referral Management)",
        "parentCompany": "Oracle Corporation",
        "category": "EHR-native referral functionality",
        "capabilitySummary": "Referral lifecycle tracking for inbound and outbound referrals, configurable in-network practitioner matching, a directory of employed and affiliated practitioners, two-way communication with community practitioners and referral-pattern analytics.",
        "deploymentModel": "Included with Oracle Health EHR, or an EHR-agnostic version for any EHR and payer.",
        "integrationNotes": "Vendor states the EHR-agnostic version works with any EHR; no Epic-specific evidence.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Oracle Health: Referral management",
            "url": "https://oracle.com/health/clinical-suite/referrals",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "NextGen Healthcare",
        "parentCompany": "Thoma Bravo (acquisition completed Nov 2023, $1.8B)",
        "category": "EHR-native referral functionality",
        "capabilitySummary": "Ambulatory EHR with closed-loop referral exchange; NextGen developers contributed to the 360X proof of concept and tested cross-EHR referrals with Epic at IHE Connectathon.",
        "deploymentModel": "Ambulatory EHR (cloud or hosted).",
        "integrationNotes": "360X testing with Epic described by vendor; relevant mainly for independent affiliate practices referring into an Epic IDN.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "NextGen blog: Making referrals safer (360X)",
            "url": "https://www.nextgen.com/blog/making-referrals-safer",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Thoma Bravo: completes acquisition of NextGen Healthcare",
            "url": "https://www.thomabravo.com/press-releases/thoma-bravo-completes-acquisition-of-nextgen-healthcare",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "symplr (symplr Directory / Provider Data Management)",
        "parentCompany": "symplr",
        "category": "Provider data, directory and interoperability infrastructure",
        "capabilitySummary": "Provider data management built from credentialing data plus location, plan participation, clinical taxonomy and availability, feeding provider directories, search, digital front door and scheduling (formerly Phynd).",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "Provider Data Connect and Schedule Advisor listed in Epic App Orchard (2021 release).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "symplr: launches symplr Directory (2021)",
            "url": "https://www.symplr.com/press-releases/symplr-launches-symplr-directory-as-part-of-end-to-end-provider-data-management-portfolio",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "H1 (formerly Ribbon Health)",
        "parentCompany": "H1 (acquired Ribbon Health, Dec 2024)",
        "category": "Provider data, directory and interoperability infrastructure",
        "capabilitySummary": "API access to a national provider and health plan directory with demographic, network, cost and quality data, used for find-care tools; now offered as H1 for Health Plans and Digital Health.",
        "deploymentModel": "Cloud API.",
        "integrationNotes": "No Epic evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Fierce Healthcare: H1 acquires Ribbon Health",
            "url": "https://www.fiercehealthcare.com/health-tech/h1-acquires-ribbon-health-expand-its-reach-health-plan-digital-health-markets",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Rhapsody (formerly Lyniate)",
        "parentCompany": "Rhapsody",
        "category": "Provider data, directory and interoperability infrastructure",
        "capabilitySummary": "Interoperability suite with Rhapsody and Corepoint integration engines, Rhapsody Identity (EMPI and provider registry) and terminology services; infrastructure for moving referral messages and provider master data rather than a referral application.",
        "deploymentModel": "On-premises, hosted or cloud integration engine.",
        "integrationNotes": "HL7 and API integration engine commonly used alongside EHRs; no referral-specific Epic evidence.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Bio-IT World: Lyniate rebrands as Rhapsody (2023)",
            "url": "https://www.bio-itworld.com/news/2023/04/04/global-interoperability-leader-lyniate-rebrands-as-rhapsody",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Salesforce Agentforce Health (formerly Health Cloud)",
        "parentCompany": "Salesforce",
        "category": "Physician relationship management / outreach CRM",
        "capabilitySummary": "Healthcare CRM with Provider Relationship and Network Management, referral prioritization and scheduling in Intelligent Appointment Management, provider search by specialty, location and availability, and referral KPIs in analytics.",
        "deploymentModel": "Cloud CRM platform, typically configured by a systems integrator.",
        "integrationNotes": "Vendor states bidirectional FHIR APIs and pre-built EHR integrations; Epic not named on pages opened.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Salesforce: Agentforce Health (formerly Health Cloud)",
            "url": "https://www.salesforce.com/ap/healthcare/cloud?d=sh",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Healthcare IT News: Salesforce provider relationship management tools (2020)",
            "url": "https://www.healthcareitnews.com/news/salesforce-unveils-new-provider-relationship-management-analytics-tools",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude",
          "ChatGPT"
        ]
      },
      {
        "name": "Microsoft Dynamics 365 / Microsoft Cloud for Healthcare",
        "parentCompany": "Microsoft",
        "category": "Physician relationship management / outreach CRM",
        "capabilitySummary": "General CRM and healthcare data model on which partners build physician referral management; example: TTEC Digital's Physician Referral Management offering on Dynamics 365 Customer Insights and Marketing with Power BI reporting.",
        "deploymentModel": "Cloud CRM platform plus partner-built configuration.",
        "integrationNotes": "No Epic-specific evidence in sources opened.",
        "typicalTimeToDeploy": "TTEC Digital partner offering is structured as an 8-week proof of concept (not a full deployment)",
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Microsoft Marketplace: TTEC Digital Physician Referral Management",
            "url": "https://marketplace.microsoft.com/en-gb/product/avtex_solutions.physician-referral-management",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Microsoft Learn: Cloud for Healthcare enhanced provider data model",
            "url": "https://learn.microsoft.com/en-us/dynamics365-release-plan/2022wave2/industry-clouds/healthcare/enhanced-provider-data-model",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Healthgrades (health system CRM, includes former Evariant)",
        "parentCompany": "RVO Health (Red Ventures and Optum partnership)",
        "category": "Physician relationship management / outreach CRM",
        "capabilitySummary": "Healthcare CRM and marketing platform for health systems (Evariant acquired in 2020) plus advertising products and Medchat automation; current physician relationship management functions were not confirmed in sources opened.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "No Epic evidence found.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Healthgrades: expands health system suite (2024-08-06)",
            "url": "https://www.healthgrades.com/about/press-room/healthgrades-expands-health-system-solutions-suite-to-enhance-patient-connections-and-operational-efficiency",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Healthgrades: next generation CRM for health systems (2018)",
            "url": "https://www.healthgrades.com/about/press-room/healthgrades-reveals-next-generation-crm-for-health-systems-at-shsmd-connections",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Hartford Business Journal: Healthgrades acquires Evariant",
            "url": "https://hartfordbusiness.com/article/denver-company-acquires-one-of-cts-fastest-growing-med-tech-firms/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "low",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "WellSky (CarePort)",
        "parentCompany": "WellSky",
        "category": "Adjacent closed-loop referral networks (post-acute and social care)",
        "capabilitySummary": "Post-acute and community referral network (130,000+ providers) for discharge referrals to SNFs, home health, rehab and community organizations, with a real-time provider directory.",
        "deploymentModel": "Cloud network SaaS embedded in the EHR.",
        "integrationNotes": "CarePort Transition Extender in Epic App Orchard (2020); CarePort Community Referral Network joined Epic Toolbox for Community Resource Network within Epic Case Management (Dec 2025).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Business Wire: WellSky joins Epic Toolbox (2025-12-10)",
            "url": "https://www.businesswire.com/news/home/20251210760242/en/WellSky-Joins-Epic-Toolbox-to-Improve-Patient-Care-Transitions-and-Coordination-Through-the-Community-Resource-Network",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Business Wire: CarePort Transition Extender in Epic App Orchard (2020)",
            "url": "https://www.businesswire.com/news/home/20200211005116/en/CarePort-Transition-Extender-Now-Available-in-the-Epic-App-Orchard-Marketplace-for-Streamlined-Referral-Management",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Unite Us",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Adjacent closed-loop referral networks (post-acute and social care)",
        "capabilitySummary": "Closed-loop referral network for health-related social needs with eligibility checks, reimbursement support and outcome tracking across healthcare and community organizations.",
        "deploymentModel": "Cloud network SaaS.",
        "integrationNotes": "Virtua Health embedding Unite Us within Epic (Feb 2025).",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Business Wire: Virtua Health partners with Unite Us (2025)",
            "url": "https://www.businesswire.com/news/home/20250211794383/en/Virtua-Health-to-Partner-with-Unite-Us-to-Address-Patients-Health-Related-Social-Needs",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "findhelp",
        "parentCompany": "Independent (ownership not verified)",
        "category": "Adjacent closed-loop referral networks (post-acute and social care)",
        "capabilitySummary": "Social care search and closed-loop referral platform used from within the patient chart.",
        "deploymentModel": "Cloud SaaS.",
        "integrationNotes": "Findhelp Native+ integrates with Epic Community Referral Network via FHIR (2022); also SMART on FHIR and Healthy Planet apps.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [],
        "sources": [
          {
            "title": "Business Wire: Findhelp native closed-loop referrals with Epic CRN (2022)",
            "url": "https://www.businesswire.com/news/home/20220224005232/en/Findhelp-Provides-Native-Social-Care-Closed-Loop-Referrals-with-Epic%E2%80%99s-Community-Referral-Network-CRN",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "Claude"
        ]
      },
      {
        "name": "Artera (formerly WELL Health)",
        "parentCompany": null,
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Referral outreach triggered by referral creation, authorization status changes and scheduling status changes, with throttled queues and conversation flows that support scheduling and referral status write-back. Covers internal and inbound external referrals; outbound external referrals are not supported.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Vendor states bidirectional integration with Epic, Cerner, Allscripts, athenahealth and MEDITECH.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "KLAS lists Artera as the 2026 Best in KLAS winner for Patient Communications.",
          "Artera reports $8M+ recovered revenue at Prisma Health from outreach and referral conversion."
        ],
        "sources": [
          {
            "title": "Artera Referral Management Overview",
            "url": "https://knowledge.artera.io/referrals/1257807-referral-management-overview",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Artera for Health Systems",
            "url": "https://artera.io/ai-health-systems/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "2026 Best in KLAS Awards: Software and Services",
            "url": "https://klasresearch.com/report/2026-best-in-klas-awards-software-and-services/3906",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "Relatient (Dash)",
        "parentCompany": null,
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "Dash platform for rules-based patient self-scheduling, two-way messaging, reminders, digital registration, triage questions, campaigns and financial clearance; 2025 additions include scheduling APIs and a voice AI agent.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Vendor states Epic App Orchard membership, HL7 FHIR API bi-directional real-time integration, and patient response data written back to Epic.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "athenahealth named Relatient a preferred partner for intelligent scheduling (April 2026)."
        ],
        "sources": [
          {
            "title": "Relatient Dash Epic Integration",
            "url": "https://www.relatient.com/dash-epic-ehr-software-integration/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "athenahealth names Relatient as preferred solution partner for intelligent scheduling (Business Wire)",
            "url": "https://businesswire.com/news/home/20260407667960/en/athenahealth-Names-Relatient-as-Preferred-Solution-Partner-for-Intelligent-Scheduling",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "ActiumHealth",
        "parentCompany": "Syllable Corporation (purchase announced March 20, 2024)",
        "category": "Patient access, navigation and scheduling",
        "capabilitySummary": "AI agents for referral activation, appointment scheduling directly in the EHR and outbound outreach, with referral status write-back from New to Scheduled/Closed.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Referral page describes detecting signed referral orders in Epic/Cerner and updating referral status in the EHR; API mechanics are not described.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Logos include MemorialCare, Nebraska Medicine, MultiCare, Legacy Health and Virtua; no referral-specific customer named."
        ],
        "sources": [
          {
            "title": "ActiumHealth Referral Management",
            "url": "https://www.actiumhealth.com/use-cases/referral-management/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "ActiumHealth",
            "url": "https://www.actiumhealth.com/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Syllable Corporation announces purchase of Actium Health (Healthcare IT Today)",
            "url": "https://www.healthcareittoday.com/2024/04/23/syllable-corporation-announces-purchase-of-actium-health/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "high",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "RubiconMD",
        "parentCompany": "CVS Health",
        "category": "Referral management and closed-loop referral platforms",
        "capabilitySummary": "National virtual specialist eConsult network giving primary care providers specialist advice, typically in under 3.5 hours, to avoid or better target in-person referrals.",
        "deploymentModel": "managed service",
        "integrationNotes": "Integrations powered by Redox; lists SAML 2.0, OAuth 2.0, HL7, FHIR, EHR-specific APIs and SCIM; Epic shown as an integration partner without detail.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Announced its one-millionth eConsult on February 4, 2026, with an Oak Street Health quote.",
          "Vendor claims up to 70% of unnecessary in-person specialist referrals avoided (vendor-reported)."
        ],
        "sources": [
          {
            "title": "RubiconMD: 1 million virtual specialty consults",
            "url": "https://www.rubiconmd.com/1-million-virtual-specialty-consults/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "RubiconMD Integrations",
            "url": "https://www.rubiconmd.com/integrations/",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "Sg2 Provider Connections",
        "parentCompany": "Vizient (Sg2 is a Vizient company)",
        "category": "Referral leakage / network integrity analytics",
        "capabilitySummary": "Claims-based application using all-payer claims for each market to find physician relationship opportunities, run referral-pattern initiatives and quantify the financial impact of network leakage.",
        "deploymentModel": "SaaS",
        "integrationNotes": "",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Provider Connections launched November 16, 2022.",
          "Sg2's current site lists Network Integrity Management (reduced leakage, more high-value referrals) and Provider Connections."
        ],
        "sources": [
          {
            "title": "Sg2 launches tool for physician engagement and network optimization",
            "url": "https://www.sg2.com/press-releases/sg2-launches-tool-physician-engagement-network-optimization",
            "retrieved": "2026-10-21"
          },
          {
            "title": "Sg2: What We Do",
            "url": "https://www.sg2.com/what-we-do",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": true,
        "confidence": "high",
        "foundBy": [
          "ChatGPT"
        ]
      },
      {
        "name": "Marketware",
        "parentCompany": "CareCloud, Inc. (via Medsphere acquisition; part of CareCloud since Aug 2025)",
        "category": "Physician relationship management / outreach CRM",
        "capabilitySummary": "Physician Strategy Suite: PRM with referral-pattern dashboards, access/service issue tracking, liaison activity planning and measurement, provider-level claims and shared-patient analysis, plus physician recruitment and onboarding.",
        "deploymentModel": "SaaS",
        "integrationNotes": "Integrates claims data into the PRM; no EHR or Epic integration named.",
        "typicalTimeToDeploy": null,
        "typicalCost": null,
        "evidence": [
          "Kalkaska Memorial Health Center quoted in CareCloud's September 25, 2025 release."
        ],
        "sources": [
          {
            "title": "Marketware Physician Relationship Management",
            "url": "https://www.marketware.com/physician-relationship-management/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "About Marketware",
            "url": "https://www.marketware.com/about/",
            "retrieved": "2026-10-21"
          },
          {
            "title": "CareCloud unveils Marketware as strategic growth solution for hospitals (GlobeNewswire)",
            "url": "https://www.globenewswire.com/news-release/2025/09/25/3156331/16541/en/CareCloud-Unveils-Marketware-as-Strategic-Growth-Solution-for-Hospitals.html",
            "retrieved": "2026-10-21"
          }
        ],
        "competitorFlag": false,
        "confidence": "medium",
        "foundBy": [
          "ChatGPT"
        ]
      }
    ],
    "deliveryPath": {
      "lean": "combined",
      "note": "Epic already provides referral orders, work queues, CERM, 360X exchange, EpicCare Link and Community Connect, so the core referral workflow is usually configured in Epic rather than bought. Gaps that IDNs commonly fill with vendors are fax-based intake automation, cross-network provider data and matching, and visibility into out-of-network destinations, which requires all-payer claims data the IDN does not hold. A build approach can join Epic referral and Clarity/Caboodle data with a licensed claims dataset for leakage reporting, but provider data maintenance and claims licensing remain ongoing costs either way."
    },
    "rfpQuestions": [
      "Does your product write referral orders, status updates and scanned documents back into Epic referral work queues and the patient chart, and through which mechanisms (FHIR, HL7, Media Manager, Epic Showroom/Vendor Services listing)?",
      "How do you measure referral loop closure (referral created, scheduled, completed, results returned) for both Epic-to-Epic and Epic-to-non-Epic referrals, including support for 360X and Direct?",
      "What data defines 'leakage' in your reporting: our EHR orders, all-payer claims, or both; what is the claims lag; and how are out-of-network destinations attributed to the ordering provider?",
      "What is the source and refresh cadence of the provider and plan participation data used for in-network matching, and how are directory errors corrected and fed back?",
      "Can matching rules prioritize employed and affiliated specialists by subspecialty, insurance, location, language and next-available capacity from Epic Cadence, and can rules be audited?",
      "Which named US Epic-based health systems of comparable size use the referral or leakage features in production, and can we speak with them?",
      "What implementation timeline, Epic build effort and internal FTE commitment should we plan for, and which parts are vendor-delivered versus our team?",
      "How is pricing structured (per provider, per referral, per site, per covered life or data license), and what are renewal terms if referral volumes change?"
    ],
    "sources": [],
    "excluded": [
      {
        "name": "Carenostics",
        "reason": "AI for chronic disease early detection; no referral management or network capability found in source opened (seedfund.nsf.gov/news/carenostics)."
      },
      {
        "name": "Qventus",
        "reason": "Perioperative and inpatient capacity automation; source opened (HIT Consultant, Jan 2025) shows no referral or specialty-access product."
      },
      {
        "name": "LeanTaaS",
        "reason": "iQueue infusion/OR/inpatient capacity scheduling; AVIA listing shows no referral or specialty-access capability."
      },
      {
        "name": "HealthSparq",
        "reason": "Acquired by Kyruus (announced Dec 2020); covered under Kyruus Health."
      },
      {
        "name": "Ribbon Health",
        "reason": "Acquired by H1 (Dec 2024); covered under H1."
      },
      {
        "name": "Evariant",
        "reason": "Acquired by Healthgrades (2020); covered under Healthgrades."
      },
      {
        "name": "Syntellis",
        "reason": "Combined with Strata under Roper Technologies (2023); covered under Strata."
      },
      {
        "name": "Preferral",
        "reason": "Acquired by AristaMD (Feb 2022); covered under AristaMD."
      },
      {
        "name": "GYANT / Zipnosis",
        "reason": "Acquired by Fabric; covered under Fabric."
      },
      {
        "name": "Loyal Health",
        "reason": "Acquired by Clarify Health (June 2026); covered under Clarify Health."
      },
      {
        "name": "Linear Health, GenHealth.ai, Emitrr, Medsender and similar AI referral-automation startups",
        "reason": "Surfaced in search results but no source opened in this pass; not verified."
      },
      {
        "name": "IQVIA Provider Relationship Manager",
        "reason": "Surfaced in search (2023 fact sheet) but not opened; appears life-sciences oriented; not verified."
      },
      {
        "name": "MEDITECH Expanse Ambulatory",
        "reason": "Proposed by ChatGPT research; not listed: Non-Epic EHR, not applicable to an Epic IDN; the page opened does not describe referral management (only referral-letter generation, e-prior-auth and Traverse Exchange)."
      }
    ]
  }
};
